import React, { useState, useMemo, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  DndContext,
  pointerWithin,
  type CollisionDetection,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragOverEvent,
  DragEndEvent,
  useDroppable,
  defaultDropAnimationSideEffects,
  DropAnimation,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { AppDialogFrame } from "../../../components/overlays/AppDialogFrame";
import { SupportErrorMessage } from "../../../components/overlays/EngineeringSupportBanner";
import {
  X,
  ChevronDown,
  LayoutGrid,
  List as ListIcon,
  Plus,
  MoreHorizontal,
  CircleCheckBig,
  CircleX,
  CircleAlert,
  ExternalLink,
  FileText,
  Download,
  Clock,
  UserCircle,
  UserPlus,
  Eye,
  Edit2,
  Calendar,
  ArrowRight,
  Archive,
  Link2,
  Mail,
  RefreshCw,
} from "lucide-react";
import type { FinancialAidReviewListItem } from "../../../state/domainRouterApi";
import {
  useGetFinancialAidApplicationForReviewQuery,
  useGetFinancialAidStatusHistoryQuery,
  useGetSettingsModuleConfigQuery,
  useListFinancialAidApplicationsForReviewQuery,
} from "../../../state/domainRouterApi";
import {
  useBeginFinancialAidReview,
  useAssignFinancialAidApplication,
  useCopyFinancialAidInviteLink,
  useFinancialAidFamilyLookup,
  useFinancialAidOpenCycle,
  useInviteFinancialAidApplication,
  useRequestFinancialAidMoreInfo,
  useResendFinancialAidInvite,
} from "../../../financial-aid/hooks/useFinancialAidApi";
import { buildFinancialAidInviteEntryUrl } from "../../../financial-aid/financialAidInviteEntryUrl";
import {
  doFinancialAidAcademicYearsMatch,
  normalizeFinancialAidAcademicYearLabel,
  resolveFinancialAidOpenCycleAcademicYear,
  resolveFinancialAidCycleStatusFromSettings,
  resolveFinancialAidStaffInviteAcademicYear,
  resolveFinancialAidStaffInviteAcademicYearInputValue,
  resolveFinancialAidStaffInviteCycleOpen,
} from "../../../financial-aid/financialAidAcademicYear";
import { parseSettingsState } from "../../../settings/settingsState";
import { Applicant } from "../types";
import { asArray, asRecord } from "../utils/domainData";
import {
  toFinancialAidStaffError,
  type StaffErrorPresentation,
} from "../utils/errorPresentation";
import {
  getFinancialAidBoardStatusGroup,
  isFinancialAidInviteApplicant,
  isFinancialAidSubmittedStatus,
  resolveFinancialAidBoardStatusTransition,
  type FinancialAidBoardStatusTransition,
} from "../utils/dashboardStatusTransitions";
import { SearchFilterControl } from "../../school-students-v2-1/components/ToolbarPrimitives";
import {
  FilterBarSection,
  FilterDropdownField,
  toSelectOptions,
} from "../../school-students-v2-1/components/FilterControls";
import { isFinancialAidAssignedStaffUserId } from "@triedge/financial-aid-domain";

type SortKey =
  | "name"
  | "studentsCount"
  | "grade"
  | "date"
  | "status"
  | "assigneeName"
  | "interviewDate"
  | null;
type SortDirection = "asc" | "desc";
type ColumnStatus = "Applied" | "In-Progress" | "Scheduled" | "Decision";
type BoardColumnCount = 1 | 2 | 3 | 4;
type DashboardViewMode = "grid" | "list";
type MoreInfoTransition = Extract<FinancialAidBoardStatusTransition, { kind: "requestMoreInfo" }>;
type PendingMoreInfoRequest = {
  applicant: Applicant;
  targetStatus: Applicant["status"] | ColumnStatus;
  transition: MoreInfoTransition;
  revertItems: Applicant[] | null;
};

const FINANCIAL_AID_DASHBOARD_VIEW_MODE_KEY =
  "financialAidDashboardViewModeV21";
const LEGACY_DASHBOARD_VIEW_MODE_KEY = "dashboardViewMode";
const BOARD_GRID_MAX_COLUMNS = 4;
const BOARD_GRID_GAP_PX = 8;
const BOARD_MIN_COLUMN_WIDTH_PX = 290;
const BOARD_GRID_DESKTOP_MIN_WIDTH_PX =
  BOARD_GRID_MAX_COLUMNS * BOARD_MIN_COLUMN_WIDTH_PX +
  (BOARD_GRID_MAX_COLUMNS - 1) * BOARD_GRID_GAP_PX;
const BOARD_COLUMN_IDS = new Set(["Applied", "In-Progress", "Scheduled", "Decision"]);
const LOOKUP_DEBOUNCE_MS = 300;

const readString = (value: unknown): string => {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") return String(value).trim();
  return "";
};

export const isFinancialAidStaffUserId = (value: unknown): value is string => {
  const normalized = readString(value);
  return isFinancialAidAssignedStaffUserId(normalized);
};

export type FinancialAidAssignableStaffOption = {
  id: string | null;
  name: string;
};

const INVITE_LIFECYCLE_LABELS: Record<string, string> = {
  invited: "Invited",
  opened: "Opened",
  started: "Started",
};

const normalizeStatusToken = (status: unknown): string =>
  readString(status)
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const isInviteLifecycleStatus = (status: unknown): boolean =>
  Object.prototype.hasOwnProperty.call(
    INVITE_LIFECYCLE_LABELS,
    normalizeStatusToken(status),
  );

const isValidInviteEmail = (email: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

const isValidAcademicYearRange = (value: string): boolean => {
  const match = value.trim().match(/^(\d{4})-(\d{4})$/);
  if (!match) return false;
  const start = Number(match[1]);
  const end = Number(match[2]);
  return Number.isInteger(start) && end === start + 1;
};

const readStaffDisplayName = (value: unknown): string => {
  const record = asRecord(value);
  const displayName = readString(record.displayName) || readString(record.name);
  if (displayName) return displayName;
  const first = readString(record.firstName);
  const last = readString(record.lastName);
  return `${first} ${last}`.trim();
};

export const toFinancialAidAssignableStaffOption = (
  entry: unknown,
): FinancialAidAssignableStaffOption | null => {
  const record = asRecord(entry);
  const name = readStaffDisplayName(entry);
  if (!name) return null;
  const rawId =
    readString(record.id) ||
    readString(record.userId) ||
    readString(record.staffId);
  return {
    id: isFinancialAidStaffUserId(rawId) ? rawId : null,
    name,
  };
};

export const mergeFinancialAidAssignableStaffOptions = (
  configuredStaffOptions: FinancialAidAssignableStaffOption[],
  applicants: Array<Pick<Applicant, "assigneeName">>,
): FinancialAidAssignableStaffOption[] => {
  const byName = new Map<string, FinancialAidAssignableStaffOption>();
  configuredStaffOptions.forEach((option) => {
    if (!byName.has(option.name)) byName.set(option.name, option);
  });
  applicants
    .map((entry) => readString(entry.assigneeName))
    .filter((name) => name.length > 0)
    .forEach((name) => {
      if (!byName.has(name)) byName.set(name, { id: null, name });
    });
  return Array.from(byName.values()).sort((left, right) => left.name.localeCompare(right.name));
};

const readNumber = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatDate = (value: unknown): string => {
  const raw = readString(value);
  if (!raw) return "";
  const numeric = readNumber(value);
  const ms =
    numeric != null
      ? numeric > 1_000_000_000_000
        ? numeric
        : numeric * 1000
      : NaN;
  const date = Number.isFinite(ms) ? new Date(ms) : new Date(raw);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const AVATAR_COLOR_PALETTE = [
  "#D3F3E7",
  "#DCE9FB",
  "#FDE8D8",
  "#F8DFF1",
  "#FDEDB0",
  "#DAF2F6",
];

const hashString = (value: string): number => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(index);
    hash |= 0;
  }
  return Math.abs(hash);
};

const buildInitials = (label: string): string => {
  const parts = label
    .split(/\s+/)
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length === 0) return "NA";
  const first = parts[0]?.[0] ?? "";
  const second =
    parts.length > 1 ? (parts[1]?.[0] ?? "") : (parts[0]?.[1] ?? "");
  const initials = `${first}${second}`.toUpperCase();
  return initials || "NA";
};

const createAvatarDataUri = (seed: string, label: string): string => {
  const token = readString(seed) || readString(label) || "avatar";
  const bg =
    AVATAR_COLOR_PALETTE[hashString(token) % AVATAR_COLOR_PALETTE.length] ??
    "#D3F3E7";
  const initials = buildInitials(label);
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='96' height='96' viewBox='0 0 96 96'><rect width='96' height='96' rx='48' fill='${bg}'/><text x='50%' y='53%' dominant-baseline='middle' text-anchor='middle' font-family='Albert Sans,sans-serif' font-size='34' font-weight='700' fill='#1B6F5A'>${initials}</text></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

const readDashboardViewMode = (): DashboardViewMode => {
  try {
    const stored = sessionStorage.getItem(
      FINANCIAL_AID_DASHBOARD_VIEW_MODE_KEY,
    );
    return stored === "list" ? "list" : "grid";
  } catch {
    return "grid";
  }
};

const persistDashboardViewMode = (viewMode: DashboardViewMode): void => {
  try {
    sessionStorage.setItem(FINANCIAL_AID_DASHBOARD_VIEW_MODE_KEY, viewMode);
    // Keep legacy key in sync for existing QA tooling that still reads this key.
    sessionStorage.setItem(LEGACY_DASHBOARD_VIEW_MODE_KEY, viewMode);
  } catch {
    // Ignore storage write failures (private mode / blocked storage).
  }
};

const toEpochMs = (value: unknown): number | null => {
  const numeric = readNumber(value);
  if (numeric != null) {
    return numeric > 1_000_000_000_000 ? numeric : numeric * 1000;
  }
  const raw = readString(value);
  if (!raw) return null;
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatDateTime = (value: unknown): string => {
  const ms = toEpochMs(value);
  if (!ms) return "";
  try {
    return new Date(ms).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

const formatActivityLabel = (value: unknown): string =>
  formatDateTime(value) || formatDate(value);

const normalizeGrade = (value: unknown): string => {
  const raw = readString(value);
  if (!raw) return "Grade K";
  const cleaned = raw.replace(/[_-]+/g, " ").trim();
  const normalized = cleaned.replace(/^grade\s*/i, "").trim();
  if (!normalized) return "Grade K";
  if (/^k(?:indergarten)?$/i.test(normalized)) return "Grade K";
  if (/^\d+$/.test(normalized)) return `Grade ${String(Number(normalized))}`;
  if (/^[a-z]$/i.test(normalized)) return `Grade ${normalized.toUpperCase()}`;
  return `Grade ${normalized.replace(/\b\w/g, (char) => char.toUpperCase())}`;
};

export const resolveFinancialAidDashboardGradeLabel = (
  value: unknown,
  _isInviteRecord = false,
): string => {
  if (!readString(value)) return "TBD";
  return normalizeGrade(value);
};

export const formatFinancialAidStudentEnrollmentLabel = (value: unknown): string => {
  const count = Math.max(0, Math.trunc(readNumber(value) ?? 0));
  return `${count} ${count === 1 ? "child" : "children"} enrolled`;
};

export const resolveFinancialAidDashboardStatus = (status: unknown): Applicant["status"] => {
  const token = normalizeStatusToken(status);
  if (!token) return "In-Progress";
  if (token === "invited" || token === "opened") return "Applied";
  if (token === "started") return "In-Progress";
  if (token === "submitted" || token === "applied" || token === "new")
    return "In-Progress";
  if (
    token === "under_review" ||
    token === "in_progress" ||
    token === "inprogress"
  )
    return "In-Progress";
  if (
    token === "more_info_needed" ||
    token === "needs_info" ||
    token === "scheduled"
  )
    return "Scheduled";
  if (token === "approved" || token === "accepted") return "Accepted";
  if (token === "denied" || token === "declined") return "Declined";
  if (token === "decision") return "Decision";
  if (token === "archived") return "Archived";
  if (token === "waitlisted") return "Waitlisted";
  if (token === "enrolled") return "Enrolled";
  return "In-Progress";
};

const statusLabelFor = ({
  rawStatus,
  status,
  progress,
  interviewDate,
}: {
  rawStatus: string;
  status: Applicant["status"];
  progress: number | undefined;
  interviewDate: string;
}): string | undefined => {
  const token = normalizeStatusToken(rawStatus);
  if (isInviteLifecycleStatus(token)) return INVITE_LIFECYCLE_LABELS[token];
  if (token === "submitted" || token === "applied" || token === "new")
    return "Submitted";
  if (token === "under_review" || token === "in_progress" || token === "inprogress")
    return "Under Review";
  if (token === "approved" || token === "accepted") return "Approved";
  if (token === "denied" || token === "declined") return "Denied";
  if (token === "more_info_needed" || token === "needs_info")
    return "More Info Needed";
  if (interviewDate) return "Urgent Priority";
  if (status === "Applied") return "Submitted";
  if (status === "In-Progress")
    return (progress ?? 0) >= 50 ? "App Review" : "Docs Pending";
  if (status === "Scheduled") return "Family Interview";
  if (status === "Decision") return "Decision";
  if (status === "Accepted") return "Approved";
  if (status === "Declined") return "Denied";
  return undefined;
};

export const projectFinancialAidReviewListItemForDashboard = (
  item: FinancialAidReviewListItem,
): Applicant => {
  const record = asRecord(item);
  const family = asRecord(record.family);
  const applicant = asRecord(record.applicant);
  const invite = asRecord(record.invite);
  const familyLink = asRecord(record.familyLink);
  const recommendation = asRecord(record.recommendation);
  const assignee = asRecord(record.assignee);
  const studentsEnrolled = asArray<Record<string, unknown>>(
    asRecord(record.familyInformation).studentsEnrolled,
  );

  const id =
    readString(record.id) ||
    readString(record.applicationId) ||
    readString(record.code);
  const rawStatus = readString(record.status);
  const statusToken = normalizeStatusToken(rawStatus);
  const status = resolveFinancialAidDashboardStatus(rawStatus);
  const invitedEmail = readString(record.invitedEmail) || readString(invite.email);
  const inviteLastName = readString(invite.lastName);
  const isInviteRecord = isInviteLifecycleStatus(statusToken);
  const familyName =
    readString(record.familyName) ||
    readString(family.familyName) ||
    readString(applicant.familyName) ||
    readString(familyLink.familyName) ||
    (isInviteRecord
      ? inviteLastName
        ? `${inviteLastName} Family`
        : invitedEmail || "Invited family"
      : "Name required");
  const assigneeName =
    readString(record.assigneeName) ||
    readString(assignee.name) ||
    "Unassigned";
  const assigneeAvatar =
    readString(record.assigneeAvatar) ||
    createAvatarDataUri(`${id}:assignee`, assigneeName);
  const submittedDate =
    formatDate(record.submittedAt) ||
    formatDate(record.createdAt) ||
    formatDate(record.updatedAt);
  const lastActivity = asRecord(record.lastActivity);
  const lastActivitySource =
    lastActivity.occurredAt ||
    record.lastActivityAt ||
    invite.lastSentAt ||
    invite.lastOpenedAt ||
    record.submittedAt ||
    record.updatedAt ||
    record.createdAt;
  const lastActivityAt = toEpochMs(lastActivitySource);
  const lastActivityLabel = formatActivityLabel(lastActivitySource) || submittedDate || "—";
  const projectedStudentCount =
    readNumber(record.childrenTotal) ??
    readNumber(record.studentCount) ??
    readNumber(record.studentsCount);
  const studentsCount =
    projectedStudentCount ?? studentsEnrolled.length ?? (isInviteRecord ? 0 : 1);
  const progressRaw =
    readNumber(record.recommendedPercent) ??
    readNumber(recommendation.recommendedPercent);
  const progress =
    progressRaw != null
      ? Math.max(0, Math.min(100, Math.round(progressRaw)))
      : undefined;
  const gradeSource =
    record.grade ||
    record.gradeApplyingFor ||
    asRecord(studentsEnrolled[0]).grade;
  const grade = resolveFinancialAidDashboardGradeLabel(gradeSource, isInviteRecord);
  const interviewDate = formatDate(
    record.interviewDate ||
      record.visitDate ||
      record.scheduledAt ||
      (status === "Scheduled"
        ? record.submittedAt || record.createdAt || record.updatedAt
        : ""),
  );
  const interviewRoom = readString(record.interviewRoom) || "TBD";
  const statusLabel = statusLabelFor({
    rawStatus,
    status,
    progress,
    interviewDate,
  });
  const statusBadgeLabel = isInviteLifecycleStatus(statusToken)
    ? INVITE_LIFECYCLE_LABELS[statusToken]
    : statusLabel;
  const staleInvite =
    record.staleInvite === true ||
    readString(record.staleInvite).toLowerCase() === "true";

  return {
    id,
    name: familyName,
    grade,
    status,
    rawStatus,
    statusLabel,
    statusBadgeLabel,
    invitedEmail: invitedEmail || undefined,
    lastActivityAt,
    lastActivityLabel,
    staleInvite,
    avatar:
      readString(record.avatar) ||
      createAvatarDataUri(id || familyName, familyName),
    date: submittedDate || lastActivityLabel || "—",
    progress,
    studentsCount,
    assigneeName,
    assigneeAvatar,
    interviewDate: interviewDate || undefined,
    interviewRoom,
  };
};

export const isFinancialAidFilteredBoardDragReadOnly = (
  shouldRenderGrid: boolean,
  isFiltered: boolean,
): boolean => shouldRenderGrid && isFiltered;

export const isFinancialAidBoardFiltered = ({
  searchQuery,
  studentsFilter,
  gradeFilter,
  statusFilter,
  assigneeFilter,
}: {
  searchQuery: string;
  studentsFilter: string;
  gradeFilter: string;
  statusFilter: string;
  assigneeFilter: string;
}): boolean => (
  searchQuery.trim().length > 0 ||
  studentsFilter !== "Students" ||
  gradeFilter !== "All Grades" ||
  statusFilter !== "All Status" ||
  assigneeFilter !== "All Staff"
);

export const buildFinancialAidMoreInfoParentMessage = ({
  applicantName,
  requestedInfo,
}: {
  applicantName: string;
  requestedInfo: string;
}): string =>
  [
    `The financial aid office needs a little more information to continue reviewing the ${applicantName} application.`,
    `Requested information: ${requestedInfo}`,
    "Please use the parent portal to review the request and upload any documents.",
  ].join("\n\n");

const readFinancialAidReviewParentEmail = (review: unknown): string => {
  const reviewRecord = asRecord(review);
  const application = asRecord(reviewRecord.application);
  const sections = asRecord(application.sections);
  const parent1 = asRecord(sections.parent1);
  const parent2 = asRecord(sections.parent2);
  return readString(parent1.email) || readString(parent2.email);
};

const toStaffDetailRoute = (applicationId: string): string =>
  `/school/financialaid/staff/${encodeURIComponent(applicationId)}/family-profile`;

const isInviteApplicant = (applicant: Applicant): boolean =>
  isFinancialAidInviteApplicant(applicant);

const getApplicantActivityMs = (applicant: Applicant): number =>
  applicant.lastActivityAt ?? toEpochMs(applicant.date) ?? 0;

const getApplicantStatusLabel = (applicant: Applicant): string =>
  applicant.statusBadgeLabel || applicant.statusLabel || applicant.status;

const resolveBoardColumnCount = (containerWidth: number): BoardColumnCount => {
  if (!Number.isFinite(containerWidth) || containerWidth <= 0) return 1;
  return containerWidth < 768 ? 1 : BOARD_GRID_MAX_COLUMNS;
};

const readBoardAvailableWidth = (boardViewport: HTMLElement): number => {
  const viewportRect = boardViewport.getBoundingClientRect();
  const viewportStyle = getComputedStyle(boardViewport);
  const paddingLeft = Number.parseFloat(viewportStyle.paddingLeft) || 0;
  const paddingRight = Number.parseFloat(viewportStyle.paddingRight) || 0;
  const contentLeft = viewportRect.left + paddingLeft;
  const contentRight = viewportRect.right - paddingRight;
  const contentWidth = Math.max(0, contentRight - contentLeft);
  const assistant = document.querySelector(
    '[data-testid="app-shell-assistant"]',
  ) as HTMLElement | null;
  if (!assistant) return contentWidth;
  if (assistant.getAttribute("data-expanded") !== "true") return contentWidth;

  const assistantRect = assistant.getBoundingClientRect();
  const assistantStyle = getComputedStyle(assistant);
  const assistantVisible =
    assistantStyle.display !== "none" &&
    assistantStyle.visibility !== "hidden" &&
    assistantRect.width > 0;
  if (!assistantVisible) return contentWidth;

  const overlap = Math.max(
    0,
    Math.min(contentRight, assistantRect.right) -
      Math.max(contentLeft, assistantRect.left),
  );
  return Math.max(0, contentWidth - overlap);
};

const SKELETON_COLUMNS: Array<{
  status: ColumnStatus;
  colorClass: string;
  count: number;
}> = [
  { status: "Applied", colorClass: "bg-aquamarine", count: 20 },
  { status: "In-Progress", colorClass: "bg-primary", count: 15 },
  { status: "Scheduled", colorClass: "bg-strawberry", count: 14 },
  { status: "Decision", colorClass: "bg-jungle", count: 31 },
];

const SKELETON_CARD_COUNT = 5;
const SKELETON_TABLE_ROWS = 10;

const SkeletonBlock: React.FC<{ className?: string }> = ({
  className = "",
}) => (
  <div
    aria-hidden
    className={`rounded-md bg-gray-100/80 animate-pulse ${className}`}
  />
);

const FinancialAidSkeletonCard: React.FC = () => (
  <div className="bg-white p-4 rounded-xl border border-gray-100 mb-3">
    <div className="flex items-start justify-between mb-3 gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <SkeletonBlock className="h-10 w-10 rounded-full shrink-0" />
        <div className="space-y-2 min-w-0">
          <SkeletonBlock className="h-3.5 w-28" />
          <SkeletonBlock className="h-3 w-20" />
        </div>
      </div>
      <SkeletonBlock className="h-4 w-4 rounded-full" />
    </div>
    <SkeletonBlock className="h-1.5 w-full rounded-full mb-3" />
    <div className="flex items-center justify-between">
      <SkeletonBlock className="h-3 w-20" />
      <SkeletonBlock className="h-3 w-8" />
    </div>
  </div>
);

const boardGridStyle = (columnCount: BoardColumnCount): React.CSSProperties => ({
  gridTemplateColumns:
    columnCount === 1
      ? "minmax(0, 1fr)"
      : `repeat(${BOARD_GRID_MAX_COLUMNS}, minmax(${BOARD_MIN_COLUMN_WIDTH_PX}px, 1fr))`,
  minWidth:
    columnCount === 1
      ? undefined
      : `${BOARD_GRID_DESKTOP_MIN_WIDTH_PX}px`,
});

const FinancialAidDashboardSkeleton: React.FC<{
  columnCount: BoardColumnCount;
}> = ({ columnCount }) => (
  <div
    data-testid="financial-aid-dashboard-skeleton"
    data-column-count={columnCount}
    className="grid w-full gap-2 items-start pb-10 h-full overflow-y-visible"
    style={boardGridStyle(columnCount)}
  >
    {SKELETON_COLUMNS.map((column) => (
      <section
        key={column.status}
        className="bg-gray-50 rounded-2xl p-3 h-full min-h-[150px] flex flex-col border border-gray-100"
        aria-label={`${column.status} loading column`}
      >
        <div className="flex items-center justify-between mb-3 px-1">
          <div className="flex items-center gap-2 min-w-0">
            <span className={`w-2 h-2 rounded-full ${column.colorClass}`} />
            <h3 className="font-semibold text-xl text-irongrey truncate">
              {column.status}
            </h3>
            <span className="bg-gray-100 text-irongrey/60 text-sm font-medium px-2 py-0.5 rounded-full">
              {column.count}
            </span>
          </div>
          <SkeletonBlock className="h-3 w-4" />
        </div>
        <div
          data-testid={
            column.status === "Applied"
              ? "financial-aid-dashboard-list"
              : undefined
          }
          className="flex-1 overflow-y-auto pr-2 pb-4 min-h-[150px]"
        >
          {Array.from({ length: SKELETON_CARD_COUNT }).map((_, index) => (
            <FinancialAidSkeletonCard
              key={`${column.status}-skeleton-${index}`}
            />
          ))}
        </div>
      </section>
    ))}
  </div>
);

const FinancialAidTableSkeleton: React.FC = () => (
  <div
    data-testid="financial-aid-table-skeleton"
    className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden h-full flex flex-col"
  >
    <div className="overflow-x-auto overflow-y-auto flex-1 pb-24">
      <table className="table-sticky-last-action-col w-full text-left border-collapse table-auto">
        <thead className="sticky top-0">
          <tr className="border-b border-gray-100 bg-gray-50/50">
            <th className="px-6 py-4 text-[10px] font-bold tracking-[0.08em] text-irongrey/35 uppercase">
              Family Name
            </th>
            <th className="px-6 py-4 text-[10px] font-bold tracking-[0.08em] text-irongrey/35 uppercase text-center">
              Students
            </th>
            <th className="px-6 py-4 text-[10px] font-bold tracking-[0.08em] text-irongrey/35 uppercase">
              Grades
            </th>
            <th className="px-6 py-4 text-[10px] font-bold tracking-[0.08em] text-irongrey/35 uppercase">
              Submitted
            </th>
            <th className="px-6 py-4 text-[10px] font-bold tracking-[0.08em] text-irongrey/35 uppercase">
              Status
            </th>
            <th className="px-6 py-4 text-[10px] font-bold tracking-[0.08em] text-irongrey/35 uppercase">
              Assignee
            </th>
            <th className="px-6 py-4 text-[10px] font-bold tracking-[0.08em] text-irongrey/35 uppercase">
              Interview
            </th>
            <th className="px-6 py-4 text-[10px] font-bold tracking-[0.08em] text-irongrey/35 uppercase text-right">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {Array.from({ length: SKELETON_TABLE_ROWS }).map((_, index) => (
            <tr key={`table-skeleton-row-${index}`} className="h-16">
              <td className="px-6 py-4">
                <div className="flex items-center gap-3">
                  <SkeletonBlock className="h-8 w-8 rounded-full shrink-0" />
                  <div className="space-y-2 min-w-0">
                    <SkeletonBlock className="h-3.5 w-24" />
                    <SkeletonBlock className="h-3 w-32" />
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 text-center">
                <SkeletonBlock className="h-5 w-6 mx-auto rounded" />
              </td>
              <td className="px-6 py-4">
                <SkeletonBlock className="h-3.5 w-16" />
              </td>
              <td className="px-6 py-4">
                <SkeletonBlock className="h-3.5 w-20" />
              </td>
              <td className="px-6 py-4">
                <SkeletonBlock className="h-5 w-16 rounded-full" />
              </td>
              <td className="px-6 py-4">
                <div className="flex items-center gap-2">
                  <SkeletonBlock className="h-5 w-5 rounded-full shrink-0" />
                  <SkeletonBlock className="h-3.5 w-20" />
                </div>
              </td>
              <td className="px-6 py-4">
                <SkeletonBlock className="h-3.5 w-20" />
              </td>
              <td className="px-6 py-4 text-right">
                <SkeletonBlock className="h-3.5 w-5 ml-auto" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const KANBAN_COLUMN_TEST_IDS: Record<string, string> = {
  Applied: "financial-aid-dashboard-column-applied",
  "In-Progress": "financial-aid-dashboard-column-in-progress",
  Scheduled: "financial-aid-dashboard-column-scheduled",
  Decision: "financial-aid-dashboard-column-decision",
};

const financialAidBoardCollisionDetection: CollisionDetection = (args) => {
  const { active } = args;
  const collisions = pointerWithin(args).filter(({ id }) => id !== active.id);
  const columnCollision = collisions.find(({ id }) => BOARD_COLUMN_IDS.has(String(id)));
  if (!columnCollision) {
    return collisions;
  }
  return [
    columnCollision,
    ...collisions.filter(({ id }) => id !== columnCollision.id),
  ];
};

// --- Sortable Card Component ---
interface SortableApplicantCardProps {
  applicant: Applicant;
  onClick: (applicationId: string) => void;
  disabled?: boolean;
}

const SortableApplicantCard: React.FC<SortableApplicantCardProps> = ({
  applicant,
  onClick,
  disabled = false,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: applicant.id,
    disabled,
    data: {
      type: "Applicant",
      applicant,
    },
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };

  return (
    <button
      type="button"
      data-testid="financial-aid-dashboard-card"
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...(disabled ? {} : listeners)}
      onClick={() => onClick(applicant.id)}
      className={`w-full text-left bg-white p-4 rounded-xl shadow-card hover:shadow-soft transition-shadow group border border-transparent hover:border-primary/20 mb-3 touch-none relative ${disabled ? "cursor-pointer" : "cursor-grab"}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <img
            alt="Student"
            className="w-10 h-10 rounded-full object-cover bg-gray-100"
            src={applicant.avatar}
          />
          <div>
            <h4 className="font-semibold text-sm text-irongrey group-hover:text-primary transition-colors">
              {applicant.name}
            </h4>
            <p className="text-xs text-irongrey/60">
              {isInviteApplicant(applicant)
                ? applicant.invitedEmail || "Invite pending"
                : formatFinancialAidStudentEnrollmentLabel(applicant.studentsCount)}
            </p>
          </div>
        </div>
        {applicant.status === "Accepted" && (
          <CircleCheckBig
            width="18"
            height="18"
            className="lucide lucide-circle-check-big text-aquamarine"
          />
        )}
        {applicant.status === "Declined" && (
          <CircleX
            width="18"
            height="18"
            className="lucide lucide-circle-x text-vibrant-coral"
          />
        )}
      </div>

      {applicant.progress !== undefined && (
        <div className="mt-2 w-full bg-gray-50 rounded-full h-1.5 mb-2 overflow-hidden">
          <div
            className="bg-primary h-1.5 rounded-full"
            style={{ width: `${applicant.progress}%` }}
          ></div>
        </div>
      )}

      {applicant.interviewDate && (
        <div className="bg-strawberry/5 rounded-lg p-2.5 flex items-center gap-3 mb-2 border border-strawberry/10">
          <div className="bg-white p-1.5 rounded-md shadow-sm text-center min-w-[40px] border border-gray-100">
            <span className="block text-[8px] uppercase font-bold text-strawberry">
              Oct
            </span>
            <span className="block text-sm font-bold text-irongrey">12</span>
          </div>
          <div>
            <p className="text-xs font-bold text-irongrey">Family Interview</p>
            <p className="text-[10px] text-irongrey/50">
              {applicant.interviewRoom}
            </p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mt-2">
        {applicant.status === "Accepted" ? (
          <>
            <span className="text-[10px] text-jungle font-bold uppercase tracking-wide">
              {getApplicantStatusLabel(applicant)}
            </span>
          </>
        ) : applicant.status === "Declined" ? (
          <>
            <span className="text-[10px] text-strawberry font-bold uppercase tracking-wide">
              {getApplicantStatusLabel(applicant)}
            </span>
            <span className="text-[10px] text-irongrey/40">Archived</span>
          </>
        ) : (
          <>
            <span
              className={`text-[10px] font-medium ${applicant.statusLabel === "Urgent Priority" || applicant.statusLabel === "Family Interview" ? "text-vibrant-coral font-bold" : "text-irongrey/40"}`}
            >
              {getApplicantStatusLabel(applicant)}
            </span>
            {applicant.staleInvite ? (
              <StaleInviteIndicator compact />
            ) : applicant.progress !== undefined ? (
              <p className="text-[10px] text-irongrey/60 font-bold">
                {applicant.progress}%
              </p>
            ) : (
              <p className="text-[10px] text-irongrey/40">{applicant.date}</p>
            )}
          </>
        )}
      </div>
    </button>
  );
};

// --- Kanban Column Component ---
const KanbanColumn: React.FC<{
  id: string;
  title: string;
  color: string;
  count: number;
  applicants: Applicant[];
  onCardClick: (applicationId: string) => void;
  listTestId?: string;
  onAddApplicant: () => void;
  onArchiveAll: () => void;
  onSortByDate: () => void;
  onMoveAll: (newStatus: Applicant["status"]) => void;
  onRenameTitle: (newTitle: string) => void;
  cardDragDisabled?: boolean;
}> = ({
  id,
  title,
  color,
  count,
  applicants,
  onCardClick,
  listTestId,
  onAddApplicant,
  onArchiveAll,
  onSortByDate,
  onMoveAll,
  onRenameTitle,
  cardDragDisabled = false,
}) => {
  const { setNodeRef } = useDroppable({
    id: id,
  });

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuStep, setMenuStep] = useState<"main" | "moveAll">("main");
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [tempTitle, setTempTitle] = useState(title);
  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
        setMenuStep("main");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isEditingTitle && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditingTitle]);

  const handleTitleSubmit = (e: React.FormEvent | React.KeyboardEvent) => {
    if (tempTitle.trim()) {
      onRenameTitle(tempTitle);
      setIsEditingTitle(false);
    }
  };

  const statusOptions: Applicant["status"][] = [
    "Applied",
    "In-Progress",
    "Scheduled",
    "Decision",
  ];
  const columnTestId = KANBAN_COLUMN_TEST_IDS[id] ?? `financial-aid-dashboard-column-${id.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;

  return (
    <div
      data-testid={columnTestId}
      className="w-full flex flex-col h-full min-w-[190px]"
    >
      <div className="flex items-center justify-between mb-4 px-1 flex-shrink-0 relative">
        <div className="flex items-center gap-2 overflow-hidden flex-1 mr-2">
          <span
            className={`w-2 h-2 rounded-full bg-${color} flex-shrink-0`}
          ></span>
          {isEditingTitle ? (
            <input
              ref={inputRef}
              className="text-sm font-bold text-irongrey bg-gray-50 border border-primary/30 rounded px-2 py-0.5 focus:ring-1 focus:ring-primary w-full"
              value={tempTitle}
              onChange={(e) => setTempTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleTitleSubmit(e);
                if (e.key === "Escape") setIsEditingTitle(false);
              }}
              onBlur={handleTitleSubmit}
            />
          ) : (
            <h3 className="font-bold text-irongrey truncate">{title}</h3>
          )}
          <span className="text-xs font-bold text-irongrey/50 bg-gray-100 px-2 py-0.5 rounded-md flex-shrink-0">
            {count}
          </span>
        </div>
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => {
              setIsMenuOpen(!isMenuOpen);
              setMenuStep("main");
            }}
            className={`text-irongrey/40 hover:text-primary transition-colors p-1 rounded-md hover:bg-gray-100 ${isMenuOpen ? "text-primary bg-primary/5" : ""}`}
          >
            <MoreHorizontal size={20} />
          </button>

          {isMenuOpen && (
            <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.15)] border border-gray-100 py-1.5 z-50 animate-in fade-in zoom-in-95 duration-100 origin-top-right">
              {menuStep === "main" ? (
                <>
                  <button
                    onClick={() => {
                      setIsMenuOpen(false);
                      setIsEditingTitle(true);
                    }}
                    className="w-full text-left px-4 py-2.5 text-xs font-bold text-irongrey hover:bg-gray-50 flex items-center gap-3 transition-colors"
                  >
                    <Edit2 size={14} className="text-irongrey/60" />
                    Edit Column
                  </button>
                  <button
                    onClick={() => {
                      setIsMenuOpen(false);
                      onAddApplicant();
                    }}
                    className="w-full text-left px-4 py-2.5 text-xs font-bold text-irongrey hover:bg-gray-50 flex items-center gap-3 transition-colors"
                  >
                    <Plus size={14} className="text-irongrey/60" />
                    Add Applicant
                  </button>
                  <button
                    onClick={() => {
                      onSortByDate();
                      setIsMenuOpen(false);
                    }}
                    className="w-full text-left px-4 py-2.5 text-xs font-bold text-irongrey hover:bg-gray-50 flex items-center gap-3 transition-colors"
                  >
                    <Calendar size={14} className="text-irongrey/60" />
                    Sort by Date
                  </button>
                  <button
                    onClick={() => setMenuStep("moveAll")}
                    className="w-full text-left px-4 py-2.5 text-xs font-bold text-irongrey hover:bg-gray-50 flex items-center gap-3 transition-colors"
                  >
                    <ArrowRight size={14} className="text-irongrey/60" />
                    Move All Cards
                  </button>
                  <div className="h-px bg-gray-50 my-1"></div>
                  <button
                    onClick={() => {
                      onArchiveAll();
                      setIsMenuOpen(false);
                    }}
                    className="w-full text-left px-4 py-2.5 text-xs font-bold text-strawberry hover:bg-strawberry/5 flex items-center gap-3 transition-colors"
                  >
                    <Archive size={14} />
                    Archive All
                  </button>
                </>
              ) : (
                <>
                  <div className="px-4 py-2 border-b border-gray-50 mb-1 flex items-center gap-2">
                    <button
                      onClick={() => setMenuStep("main")}
                      className="text-irongrey/40 hover:text-primary"
                    >
                      <ChevronDown size={14} className="rotate-90" />
                    </button>
                    <span className="text-[10px] font-bold text-irongrey/40 uppercase tracking-widest">
                      Move All to...
                    </span>
                  </div>
                  {statusOptions.map((opt) => (
                    <button
                      key={opt}
                      onClick={() => {
                        onMoveAll(opt);
                        setIsMenuOpen(false);
                        setMenuStep("main");
                      }}
                      className="w-full text-left px-4 py-2 text-xs font-medium hover:bg-gray-50 text-irongrey"
                    >
                      {opt}
                    </button>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <div
        ref={setNodeRef}
        data-testid={listTestId ?? `${columnTestId}-dropzone`}
        className="flex-1 overflow-y-auto pr-2 pb-4 min-h-[150px]"
      >
        <SortableContext
          items={applicants.map((a) => a.id)}
          strategy={verticalListSortingStrategy}
        >
          {applicants.map((applicant) => (
            <SortableApplicantCard
              key={applicant.id}
              applicant={applicant}
              onClick={onCardClick}
              disabled={cardDragDisabled}
            />
          ))}
        </SortableContext>
      </div>
    </div>
  );
};

// --- Utils & Subcomponents ---

interface CustomDropdownProps {
  label?: string;
  options: string[];
  value: string;
  onChange: (val: string) => void;
  className?: string;
  placeholder?: string;
  testId?: string;
  menuTestId?: string;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  label,
  options,
  value,
  onChange,
  className,
  placeholder,
  testId,
  menuTestId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const closeDropdown = () => {
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    setIsOpen((current) => !current);
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeDropdown();
      }
    };
    const handlePointerDown = (event: MouseEvent) => {
      if (
        containerRef.current &&
        event.target instanceof Node &&
        !containerRef.current.contains(event.target)
      ) {
        closeDropdown();
      }
    };
    const handleWindowResize = () => closeDropdown();

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handlePointerDown);
    window.addEventListener("resize", handleWindowResize);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handlePointerDown);
      window.removeEventListener("resize", handleWindowResize);
    };
  }, [isOpen]);

  return (
    <div
      ref={containerRef}
      className={`relative flex items-center ${className}`}
    >
      {label && (
        <span className="text-[10px] font-bold text-irongrey/40 uppercase tracking-tight whitespace-nowrap mr-2 hidden xl:block">
          {label}
        </span>
      )}
      <button
        ref={buttonRef}
        data-testid={testId}
        onClick={toggleDropdown}
        className="flex items-center justify-between gap-2 text-sm font-semibold text-irongrey hover:text-primary transition-colors w-full px-3 py-2 rounded-lg"
      >
        <span className="truncate">{value || placeholder || "Select..."}</span>
        <ChevronDown
          size={14}
          className={`transition-transform duration-200 text-irongrey/40 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen ? (
        <div
          ref={dropdownRef}
          data-testid={menuTestId ?? (testId ? `${testId}-menu` : undefined)}
          className="absolute top-full left-0 mt-2 min-w-[160px] bg-white rounded-xl shadow-soft border border-gray-100 py-1 z-[1400] animate-in fade-in zoom-in-95 duration-200"
          style={{ width: Math.max(buttonRef.current?.offsetWidth ?? 0, 200) }}
        >
          <div className="max-h-60 overflow-y-auto">
            {options.map((option) => (
              <button
                key={option}
                className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${value === option ? "text-primary font-bold bg-primary/5" : "text-irongrey"}`}
                onClick={(event) => {
                  event.stopPropagation();
                  onChange(option);
                  closeDropdown();
                }}
              >
                {option}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const CREATE_NEW_FAMILY_SELECTION = "__create_new_financial_aid_family__";

export const AddApplicantModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onCreated?: () => void;
  onFeedback?: (feedback: { tone: "success" | "error"; message: string; technicalDetails?: string }) => void;
}> = ({ isOpen, onClose, onCreated, onFeedback }) => {
  const navigate = useNavigate();
  const openCycle = useFinancialAidOpenCycle();
  const settingsConfig = useGetSettingsModuleConfigQuery({
    moduleKey: "staff_settings",
  });
  const inviteMutation = useInviteFinancialAidApplication();
  const copyLinkMutation = useCopyFinancialAidInviteLink();
  const openCycleRecord = asRecord(openCycle.data);
  const settingsCycleAuthorityReady = Boolean(settingsConfig.data)
    && !settingsConfig.isLoading
    && !settingsConfig.isFetching
    && !settingsConfig.error;
  const settingsState = useMemo(
    () => settingsCycleAuthorityReady
      ? parseSettingsState(asRecord(settingsConfig.data).payload)
      : null,
    [settingsConfig.data, settingsCycleAuthorityReady],
  );
  const openCycleAcademicYear = resolveFinancialAidOpenCycleAcademicYear(openCycleRecord);
  const openCycleLookupSettled = !openCycle.isLoading && !openCycle.error;
  const cycleReady = Boolean(openCycle.data) && openCycleLookupSettled;
  const resolvedDefaultAcademicYear = settingsCycleAuthorityReady
    ? (
        resolveFinancialAidStaffInviteAcademicYear({
          openCycle: openCycleRecord,
          openCycleReady: openCycleLookupSettled,
          settingsState,
        }) || null
      )
    : null;
  const noOpenCycleAuthorityReady = settingsCycleAuthorityReady
    && openCycleLookupSettled
    && !resolvedDefaultAcademicYear;
  const openCycleAuthorityUnavailable = Boolean(openCycle.error);
  const settingsCycleAuthorityUnavailable = Boolean(settingsConfig.error);
  const cycleAuthorityGuardActive = noOpenCycleAuthorityReady
    || openCycleAuthorityUnavailable
    || settingsCycleAuthorityUnavailable;
  const defaultAcademicYear = resolvedDefaultAcademicYear || "";
  const [formData, setFormData] = useState({
    academicYear: defaultAcademicYear,
    primaryEmail: "",
    lastName: "",
  });
  const [selectedFamilyId, setSelectedFamilyId] = useState("");
  const [submitError, setSubmitError] = useState<StaffErrorPresentation | null>(null);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const [copyStatusMessage, setCopyStatusMessage] = useState<string | null>(null);
  const [isCopyingGenericInviteLink, setIsCopyingGenericInviteLink] = useState(false);
  const [debouncedLookupEmail, setDebouncedLookupEmail] = useState("");
  const [debouncedFamilySearchTerm, setDebouncedFamilySearchTerm] = useState("");
  const [inviteResult, setInviteResult] = useState<{
    applicationId: string;
    email: string;
    inviteUrl: string;
    statusLabel: string;
    emailQueued: boolean;
    emailQueueReason?: string | null;
    emailQueueMessage?: string | null;
  } | null>(null);
  const academicYearManuallyEditedRef = useRef(false);
  const modalOpenRef = useRef(isOpen);
  const modalSessionIdRef = useRef(0);

  const normalizedEmail = formData.primaryEmail.trim().toLowerCase();
  const normalizedEmailValid = isValidInviteEmail(normalizedEmail);
  const normalizedAcademicYear = normalizeFinancialAidAcademicYearLabel(formData.academicYear);
  const academicYear = normalizedAcademicYear || formData.academicYear.trim();
  const familySearchTerm = formData.lastName.trim();
  const settledLookupEmail = normalizedEmailValid && debouncedLookupEmail === normalizedEmail
    ? debouncedLookupEmail
    : "";
  const settledFamilySearchTerm = debouncedFamilySearchTerm.trim();
  const familyLookupSettled = settledFamilySearchTerm === familySearchTerm;
  const emailLookupSettled = !normalizedEmailValid || debouncedLookupEmail === normalizedEmail;
  const lookupInputSettled = familyLookupSettled && emailLookupSettled;
  const lookupHasSearchIntent = normalizedEmailValid || familySearchTerm.length >= 2;
  const selectedSettingsCycleAcademicYear = normalizeFinancialAidAcademicYearLabel(
    readString(asRecord(asRecord(asRecord(settingsState).financialAid).cycle).schoolYear),
  );
  const selectedSettingsCycleStatus = useMemo(
    () => selectedSettingsCycleAcademicYear
      ? resolveFinancialAidCycleStatusFromSettings(settingsState, selectedSettingsCycleAcademicYear)
      : null,
    [selectedSettingsCycleAcademicYear, settingsState],
  );
  const selectedCycleStatus = useMemo(
    () => resolveFinancialAidCycleStatusFromSettings(settingsState, academicYear),
    [academicYear, settingsState],
  );
  const hasValidLookupEmail = Boolean(settledLookupEmail);
  const hasFamilySearchTerm = settledFamilySearchTerm.length >= 2;
  const lookupEnabled = isOpen && lookupInputSettled && (hasValidLookupEmail || hasFamilySearchTerm);
  const familyLookup = useFinancialAidFamilyLookup(
    {
      email: hasValidLookupEmail ? settledLookupEmail : undefined,
      lastName: hasFamilySearchTerm ? settledFamilySearchTerm : undefined,
      academicYear: academicYear,
      enabled: lookupEnabled,
    },
  );

  const lookupMatches = useMemo(
    () => {
      if (!lookupEnabled || !lookupInputSettled || familyLookup.isLoading || familyLookup.error) {
        return [];
      }
      return asArray<Record<string, unknown>>(asRecord(familyLookup.data).matches)
        .map((entry) => ({
          familyId: readString(entry.familyId),
          familyName: readString(entry.familyName),
          applicantId: readString(entry.applicantId),
          parentFirstName: readString(entry.parentFirstName),
          parentLastName: readString(entry.parentLastName),
          email: readString(entry.email),
          studentCount: readNumber(entry.studentCount) ?? 0,
          existingApplication: (() => {
            const existingApplication = asRecord(entry.existingApplication);
            const applicationId = readString(existingApplication.applicationId);
            const status = readString(existingApplication.status);
            const existingAcademicYear = readString(existingApplication.academicYear);
            const invitedEmail = readString(existingApplication.invitedEmail);
            return applicationId && status
              ? {
                  applicationId,
                  status,
                  academicYear: existingAcademicYear,
                  invitedEmail,
                }
              : null;
          })(),
          matchType: readString(entry.matchType),
          inviteApplicationId: readString(entry.inviteApplicationId),
        }))
        .filter((entry) => entry.familyId && entry.familyName);
    },
    [familyLookup.data, familyLookup.error, familyLookup.isLoading, lookupEnabled, lookupInputSettled],
  );
  const lookupBusy = familyLookup.isLoading || (isOpen && lookupHasSearchIntent && !lookupInputSettled);
  const lookupTotalCount = readNumber(asRecord(familyLookup.data).totalCount) ?? lookupMatches.length;
  const lookupResultReady = lookupEnabled && lookupInputSettled && !lookupBusy && !familyLookup.error;

  useEffect(() => {
    const wasOpen = modalOpenRef.current;
    if (isOpen && !wasOpen) {
      modalSessionIdRef.current += 1;
      academicYearManuallyEditedRef.current = false;
      setFormData({
        academicYear: cycleAuthorityGuardActive ? "" : defaultAcademicYear,
        primaryEmail: "",
        lastName: "",
      });
      setSelectedFamilyId("");
      setSubmitError(null);
      setCopyMessage(null);
      setCopyStatusMessage(null);
      setIsCopyingGenericInviteLink(false);
      setDebouncedLookupEmail("");
      setDebouncedFamilySearchTerm("");
      setInviteResult(null);
    }
    if (!isOpen && wasOpen) {
      academicYearManuallyEditedRef.current = false;
    }
    modalOpenRef.current = isOpen;
  }, [cycleAuthorityGuardActive, defaultAcademicYear, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const timeout = window.setTimeout(() => {
      setDebouncedFamilySearchTerm(familySearchTerm);
    }, LOOKUP_DEBOUNCE_MS);
    return () => window.clearTimeout(timeout);
  }, [familySearchTerm, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const timeout = window.setTimeout(() => {
      setDebouncedLookupEmail(isValidInviteEmail(normalizedEmail) ? normalizedEmail : "");
    }, LOOKUP_DEBOUNCE_MS);
    return () => window.clearTimeout(timeout);
  }, [normalizedEmail, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (noOpenCycleAuthorityReady) {
      academicYearManuallyEditedRef.current = false;
    }
    setFormData((current) => {
      const nextAcademicYear = resolveFinancialAidStaffInviteAcademicYearInputValue({
        currentAcademicYear: current.academicYear,
        defaultAcademicYear,
        guardedCycleAuthority: noOpenCycleAuthorityReady,
        manuallyEdited: academicYearManuallyEditedRef.current,
        noOpenCycleAuthorityReady,
      });
      return current.academicYear === nextAcademicYear
        ? current
        : {
            ...current,
            academicYear: nextAcademicYear,
          };
    });
  }, [cycleAuthorityGuardActive, defaultAcademicYear, isOpen, noOpenCycleAuthorityReady]);

  useEffect(() => {
    if (!isOpen) return;
    if (!lookupResultReady) return;
    if (lookupMatches.length === 0) {
      setSelectedFamilyId("");
      return;
    }
    const selectedStillExists = lookupMatches.some(
      (match) => match.familyId === selectedFamilyId,
    ) || selectedFamilyId === CREATE_NEW_FAMILY_SELECTION;
    if (lookupMatches.length === 1 && (!selectedFamilyId || !selectedStillExists)) {
      setSelectedFamilyId(lookupMatches[0]?.familyId ?? "");
      return;
    }
    if (!selectedStillExists) {
      setSelectedFamilyId("");
    }
  }, [isOpen, lookupMatches, lookupResultReady, selectedFamilyId]);

  const handleClose = () => {
    modalOpenRef.current = false;
    academicYearManuallyEditedRef.current = false;
    setFormData({
      academicYear: defaultAcademicYear,
      primaryEmail: "",
      lastName: "",
    });
    setSelectedFamilyId("");
    setSubmitError(null);
    setCopyMessage(null);
    setCopyStatusMessage(null);
    setIsCopyingGenericInviteLink(false);
    setDebouncedLookupEmail("");
    setDebouncedFamilySearchTerm("");
    setInviteResult(null);
    onClose();
  };

  const selectedMatch =
    selectedFamilyId === CREATE_NEW_FAMILY_SELECTION
      ? null
      : lookupMatches.find((match) => match.familyId === selectedFamilyId) ?? null;
  const selectedExistingApplication = selectedMatch?.existingApplication ?? null;
  const selectedExistingApplicationStatus = normalizeStatusToken(selectedExistingApplication?.status);
  const selectedExistingApplicationCanResend = Boolean(
    selectedExistingApplication && isInviteLifecycleStatus(selectedExistingApplicationStatus),
  );
  const selectedExistingApplicationReusable =
    !selectedExistingApplication || selectedExistingApplicationCanResend;
  const lookupDecisionRequired = lookupMatches.length > 1
    && !selectedMatch
    && selectedFamilyId !== CREATE_NEW_FAMILY_SELECTION;
  const lookupErrorBlocksSubmit = Boolean(familyLookup.error && lookupHasSearchIntent);
  const academicYearValid = Boolean(normalizedAcademicYear);
  const selectedCycleOpen = settingsCycleAuthorityReady && resolveFinancialAidStaffInviteCycleOpen({
    academicYear,
    openCycle: openCycleRecord,
    openCycleReady: openCycleLookupSettled,
    settingsState,
  });
  const selectedCycleResolved = settingsCycleAuthorityReady && (
    Boolean(selectedCycleStatus) || (cycleReady && Boolean(openCycleAcademicYear))
  );
  const cycleClosed = academicYearValid && selectedCycleResolved && !selectedCycleOpen;
  const noOpenCycleAvailable = isOpen && noOpenCycleAuthorityReady;
  const academicYearMatchesDefault = doFinancialAidAcademicYearsMatch(academicYear, defaultAcademicYear);
  const closedSelectedSettingsCycleFallback = Boolean(
    isOpen
      && selectedSettingsCycleAcademicYear
      && defaultAcademicYear
      && academicYearMatchesDefault
      && selectedSettingsCycleStatus?.isClosed
      && !doFinancialAidAcademicYearsMatch(selectedSettingsCycleAcademicYear, defaultAcademicYear),
  );
  const settingsCycleAuthorityPending = isOpen
    && !settingsConfig.error
    && (
      settingsConfig.isLoading
      || settingsConfig.isFetching
      || !settingsConfig.data
    );
  const showLookupWarning = Boolean(
    lookupEnabled
      && familyLookup.error
      && !lookupBusy
      && !submitError
      && lookupMatches.length === 0,
  );
  const canCopyGenericInviteLink =
    academicYearValid &&
    settingsCycleAuthorityReady &&
    selectedCycleOpen &&
    !cycleAuthorityGuardActive &&
    !isCopyingGenericInviteLink &&
    !copyLinkMutation.isPending &&
    !inviteMutation.isPending;
  const canSubmit =
    academicYearValid &&
    settingsCycleAuthorityReady &&
    isValidInviteEmail(normalizedEmail) &&
    !lookupDecisionRequired &&
    !lookupErrorBlocksSubmit &&
    selectedExistingApplicationReusable &&
    lookupInputSettled &&
    !lookupBusy &&
    selectedCycleOpen &&
    !cycleAuthorityGuardActive &&
    !inviteMutation.isPending &&
    !copyLinkMutation.isPending;

  const lookupMatchLabel = (match: { matchType?: string }): string => {
    const matchType = normalizeStatusToken(match.matchType);
    if (matchType === "email" || matchType === "email_last_name") {
      return "Email match";
    }
    if (matchType === "family_name") {
      return "Family-name match";
    }
    return "Family record";
  };

  const lookupMatchDescription = (match: { matchType?: string }): string => {
    const matchType = normalizeStatusToken(match.matchType);
    if (matchType === "email" || matchType === "email_last_name") {
      return "Matched by parent email";
    }
    if (matchType === "family_name" && hasValidLookupEmail && lookupResultReady) {
      return "Matched by family name. Parent email did not match this family.";
    }
    if (matchType === "family_name") {
      return "Matched by family name";
    }
    return "Matched family record";
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      setSubmitError({
        message: settingsCycleAuthorityUnavailable
          ? "We couldn't load financial aid cycle settings. Try again before sending."
          : settingsCycleAuthorityPending
            ? "Financial aid cycle settings are still loading. Try again in a moment."
            : cycleClosed
          ? "The selected financial aid cycle is closed. Review cycle settings before sending."
              : !selectedCycleOpen
                ? "Choose an academic year with an open financial aid cycle before sending."
                : lookupErrorBlocksSubmit
                  ? "We couldn't check existing families right now. Try again before sending."
                : !selectedExistingApplicationReusable
                  ? "This family already has a submitted financial aid application for this year. Open it instead of sending another invite."
              : lookupDecisionRequired
                ? "Choose a matching family, or choose to send this to a new financial aid family."
              : "Enter a valid parent email before sending. You can search by family name first.",
      });
      return;
    }

    setSubmitError(null);
    setCopyMessage(null);
    setCopyStatusMessage(null);
    try {
      const application = await inviteMutation.mutateAsync({
        academicYear,
        primaryEmail: normalizedEmail,
        ...(familySearchTerm
          ? { lastName: familySearchTerm }
          : selectedMatch?.inviteApplicationId && selectedMatch.parentLastName
            ? { lastName: selectedMatch.parentLastName }
            : {}),
        // Pre-submission invite matches have no Students/Families record, so
        // their familyId is the invite application id and must not be sent as
        // a family link; the backend reuses the invite by email + cycle.
        ...(selectedMatch && !selectedMatch.inviteApplicationId
          ? {
              familyId: selectedMatch.familyId,
              familyName: selectedMatch.familyName,
              ...(selectedMatch.applicantId
                ? { applicantId: selectedMatch.applicantId }
                : {}),
            }
          : {}),
      });
      const applicationRecord = asRecord(application);
      const applicationId = readString(applicationRecord.id);
      const inviteRecord = asRecord(applicationRecord.invite);
      const inviteToken = readString(inviteRecord.token);
      const returnedInviteEmail = readString(inviteRecord.email) || normalizedEmail;
      const rawStatus = readString(applicationRecord.status) || "invited";
      const emailQueued = applicationRecord.emailQueued === false ? false : true;
      const emailQueueMessage = readString(applicationRecord.emailQueueMessage);
      const inviteUrl = buildFinancialAidInviteEntryUrl({
        academicYear,
        inviteToken,
      });
      setInviteResult({
        applicationId,
        email: returnedInviteEmail,
        inviteUrl,
        statusLabel: INVITE_LIFECYCLE_LABELS[normalizeStatusToken(rawStatus)] || "Invited",
        emailQueued,
        emailQueueReason: readString(applicationRecord.emailQueueReason) || null,
        emailQueueMessage: emailQueueMessage || null,
      });
      const copied = inviteUrl ? await copyInviteUrlToClipboard(inviteUrl) : false;
      if (copied) {
        recordInviteCopyActivity({ applicationId, feedbackTarget: emailQueued ? "dashboard" : "silent" });
      }
      if (emailQueued) {
        onFeedback?.({
          tone: "success",
          message: copied
            ? `Invite sent to ${returnedInviteEmail}. Link copied to clipboard.`
            : `Invite sent to ${returnedInviteEmail}. If the link was not copied, use Copy Link from the invited row.`,
        });
      } else {
        onFeedback?.({
          tone: "error",
          message: emailQueueMessage
            ? `Invite created for ${returnedInviteEmail}. ${emailQueueMessage}`
            : `Invite created for ${returnedInviteEmail}, but the email was not queued. Use Copy Link from the invited row to share it.`,
          technicalDetails: readString(applicationRecord.emailQueueReason) || undefined,
        });
      }
      onCreated?.();
      handleClose();
    } catch (error) {
      setSubmitError(toFinancialAidStaffError(
        error,
        "We couldn't send the invite right now. Please try again.",
      ));
    }
  };

  const copyInviteUrlToClipboard = async (inviteUrl: string) => {
    if (!inviteUrl) {
      setSubmitError({
        message: "The invite was created, but we couldn't prepare the link. Try again, or ask an administrator to check the financial aid portal setup.",
      });
      return false;
    }

    let copied = false;
    if (typeof navigator !== "undefined" && navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(inviteUrl);
        copied = true;
      } catch {
        copied = false;
      }
    }

    if (!copied && typeof document !== "undefined") {
      const field = document.createElement("textarea");
      field.value = inviteUrl;
      field.setAttribute("readonly", "true");
      field.style.position = "fixed";
      field.style.left = "-9999px";
      field.style.top = "0";
      document.body.appendChild(field);
      field.focus();
      field.select();
      try {
        copied = document.execCommand("copy");
      } catch {
        copied = false;
      } finally {
        document.body.removeChild(field);
      }
    }

    setCopyMessage(inviteUrl);
    if (copied) {
      setCopyStatusMessage("Invitation link copied to your clipboard.");
    }
    if (!copied) {
      setCopyStatusMessage("Invitation link is ready. Use the link shown here if it was not copied automatically.");
    }
    return copied;
  };

  const recordInviteCopyActivity = (input: {
    academicYear?: string;
    applicationId?: string;
    feedbackTarget?: "modal" | "dashboard" | "silent";
    modalSessionId?: number;
  }) => {
    const {
      feedbackTarget = "modal",
      modalSessionId = modalSessionIdRef.current,
      ...copyActivityInput
    } = input;
    void copyLinkMutation.mutateAsync(copyActivityInput).catch((error) => {
      if (feedbackTarget === "silent") {
        return;
      }
      if (feedbackTarget === "dashboard" || !modalOpenRef.current || modalSessionId !== modalSessionIdRef.current) {
        const presentation = toFinancialAidStaffError(
          error,
          "Invite link ready, but we couldn't save the copy activity note. The parent link can still be shared.",
        );
        onFeedback?.({ tone: "error", ...presentation });
        return;
      }
      setSubmitError(
        toFinancialAidStaffError(
          error,
          "The invite link is ready. We couldn't save the audit note for this copy action, but you can still share the link.",
        ),
      );
    });
  };

  const handleCopyGenericInviteLink = async () => {
    if (!canCopyGenericInviteLink) {
      setSubmitError({
        message: settingsCycleAuthorityUnavailable
          ? "We couldn't load financial aid cycle settings. Try again before copying."
          : settingsCycleAuthorityPending
            ? "Financial aid cycle settings are still loading. Try again in a moment."
            : cycleClosed
          ? "The selected financial aid cycle is closed. Review cycle settings before copying."
          : !selectedCycleOpen
            ? "Choose an academic year with an open financial aid cycle before copying."
            : "Choose a valid open academic year before copying the invitation link.",
      });
      return;
    }
    setCopyMessage(null);
    setCopyStatusMessage(null);
    setSubmitError(null);
    setIsCopyingGenericInviteLink(true);
    try {
      const result = await copyLinkMutation.mutateAsync({ academicYear });
      const inviteUrl = readString(asRecord(result).inviteUrl);
      await copyInviteUrlToClipboard(inviteUrl);
    } catch (error) {
      setSubmitError(toFinancialAidStaffError(
        error,
        "We couldn't copy the invitation link right now. Please try again.",
      ));
    } finally {
      setIsCopyingGenericInviteLink(false);
    }
  };

  const handleCopyInviteLink = async () => {
    if (!inviteResult?.applicationId || copyLinkMutation.isPending) return;
    setCopyMessage(null);
    setCopyStatusMessage(null);
    setSubmitError(null);
    try {
      if (inviteResult.inviteUrl) {
        await copyInviteUrlToClipboard(inviteResult.inviteUrl);
        recordInviteCopyActivity({
          applicationId: inviteResult.applicationId,
          modalSessionId: modalSessionIdRef.current,
        });
      } else {
        const result = await copyLinkMutation.mutateAsync({
          applicationId: inviteResult.applicationId,
        });
        await copyInviteUrlToClipboard(readString(asRecord(result).inviteUrl));
      }
      onCreated?.();
    } catch (error) {
      setSubmitError(toFinancialAidStaffError(
        error,
        "We couldn't copy the invite link right now. Please try again.",
      ));
    }
  };

  if (!isOpen) return null;

  return (
    <AppDialogFrame
      open={isOpen}
      onClose={handleClose}
      title="Invite Financial Aid Family"
      description="Send or reuse a pre-submission invite for the selected cycle."
      testId="financial-aid-add-application-modal"
      maxWidthClassName="max-w-2xl"
      titleClassName="text-xl"
      descriptionClassName="text-xs font-medium text-irongrey/40"
      bodyClassName="space-y-6"
      bodyPaddingClassName="p-8"
      footerPaddingClassName="px-8 py-5"
      footer={
        <>
          <button
            onClick={handleClose}
            className="px-6 py-2 text-sm font-bold text-irongrey/60 transition-colors hover:text-irongrey"
          >
            {inviteResult ? "Done" : "Cancel"}
          </button>
          {inviteResult ? (
            <button
              onClick={handleCopyInviteLink}
              disabled={copyLinkMutation.isPending}
              className="flex items-center gap-2 rounded-xl bg-primary px-8 py-2 text-sm font-bold text-white shadow-lg shadow-primary/20 transition-all hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Link2 size={16} />
              {copyLinkMutation.isPending ? "Copying..." : "Copy Link"}
            </button>
          ) : (
            <>
              <button
                onClick={handleCopyGenericInviteLink}
                disabled={!canCopyGenericInviteLink}
                className="flex items-center gap-2 rounded-xl border border-primary/20 bg-white px-6 py-2 text-sm font-bold text-primary transition-all hover:border-primary/40 hover:bg-primary/5 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Link2 size={16} />
                {isCopyingGenericInviteLink ? "Copying..." : "Copy invitation link"}
              </button>
              <button
                onMouseDown={(event) => event.preventDefault()}
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="flex items-center gap-2 rounded-xl bg-primary px-8 py-2 text-sm font-bold text-white shadow-lg shadow-primary/20 transition-all hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Mail size={16} />
                {inviteMutation.isPending ? "Sending..." : selectedExistingApplicationCanResend ? "Resend Invite" : "Send Invite"}
              </button>
            </>
          )}
        </>
      }
    >
      <div className="grid gap-4 md:grid-cols-[1fr_1.2fr_1fr]">
        <div>
          <label className="text-[10px] font-bold text-irongrey/40 uppercase tracking-widest mb-2 block">
            Academic Year
          </label>
          <input
            type="text"
            aria-label="Academic Year"
            data-testid="financial-aid-invite-academic-year"
            className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-sm font-semibold text-irongrey focus:ring-primary focus:border-primary placeholder:text-irongrey/30"
            placeholder={cycleAuthorityGuardActive
              ? noOpenCycleAuthorityReady
                ? "No open cycle"
                : "Cycle unavailable"
              : "2026-2027"}
            value={formData.academicYear}
            disabled={cycleAuthorityGuardActive}
            onChange={(event) => {
              academicYearManuallyEditedRef.current = true;
              setFormData({ ...formData, academicYear: event.target.value });
              setSelectedFamilyId("");
              setInviteResult(null);
              setCopyMessage(null);
              setCopyStatusMessage(null);
            }}
          />
        </div>
        <div>
          <label className="text-[10px] font-bold text-irongrey/40 uppercase tracking-widest mb-2 block">
            Parent Email
          </label>
          <input
            type="email"
            className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-sm font-semibold text-irongrey focus:ring-primary focus:border-primary placeholder:text-irongrey/30"
            placeholder="parent@example.com"
            value={formData.primaryEmail}
            onChange={(event) => {
              const nextEmail = event.target.value;
              setFormData({ ...formData, primaryEmail: nextEmail });
              setInviteResult(null);
              setCopyMessage(null);
              setCopyStatusMessage(null);
            }}
            onBlur={() => {
              setDebouncedLookupEmail(isValidInviteEmail(normalizedEmail) ? normalizedEmail : "");
            }}
          />
        </div>
        <div>
          <label className="text-[10px] font-bold text-irongrey/40 uppercase tracking-widest mb-2 block">
            Family Name
          </label>
          <input
            type="text"
            className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-sm font-semibold text-irongrey focus:ring-primary focus:border-primary placeholder:text-irongrey/30"
            placeholder="Cohen Family"
            value={formData.lastName}
            onChange={(event) => {
              setFormData({ ...formData, lastName: event.target.value });
              setSelectedFamilyId("");
              setInviteResult(null);
              setCopyMessage(null);
              setCopyStatusMessage(null);
            }}
          />
        </div>
      </div>

      {settingsCycleAuthorityPending ? (
        <div
          data-testid="financial-aid-cycle-settings-loading"
          className="rounded-xl border border-primary/15 bg-primary/5 px-4 py-3 text-xs font-semibold text-primary"
        >
          Loading financial aid cycle settings before invites can be sent.
        </div>
      ) : null}

      {settingsCycleAuthorityUnavailable ? (
        <SupportErrorMessage>
          We couldn't load financial aid cycle settings. Try closing and reopening this invite window before sending or copying an invite.
        </SupportErrorMessage>
      ) : null}

      {openCycleAuthorityUnavailable ? (
        <SupportErrorMessage>
          We couldn't load financial aid open-cycle authority. Try closing and reopening this invite window before sending or copying an invite.
        </SupportErrorMessage>
      ) : null}

      {cycleClosed ? (
        <SupportErrorMessage>
          The selected financial aid cycle is closed. Review cycle settings before sending new invites.
        </SupportErrorMessage>
      ) : null}

      {closedSelectedSettingsCycleFallback ? (
        <div
          data-testid="financial-aid-closed-settings-cycle-fallback"
          className="rounded-xl border border-primary/15 bg-primary/5 px-4 py-3 text-xs font-semibold leading-5 text-primary"
        >
          The selected Financial Aid Settings cycle {selectedSettingsCycleAcademicYear} is closed, so new invites default to {defaultAcademicYear}, the latest configured open cycle.
        </div>
      ) : null}

      {noOpenCycleAvailable ? (
        <SupportErrorMessage>
          No financial aid cycles are currently open. Open a configured cycle in Financial Aid settings before sending or copying invites.
        </SupportErrorMessage>
      ) : null}

      <div className="rounded-2xl border border-gray-100 bg-gray-50/70 p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-bold text-irongrey">Family lookup</h3>
            <p className="text-xs text-irongrey/50">
              Search by family name, or enter a valid parent email to check family records.
            </p>
          </div>
          {lookupBusy ? (
            <span className="text-[10px] font-bold uppercase tracking-widest text-primary">
              Searching
            </span>
          ) : null}
        </div>

        {!lookupHasSearchIntent ? (
          <p className="mt-4 text-xs font-semibold text-irongrey/50">
            Enter a family name, or enter a valid parent email, to search existing family records.
          </p>
        ) : lookupBusy ? (
          <p className="mt-4 text-xs font-semibold text-irongrey/50">
            Searching existing family records...
          </p>
        ) : lookupMatches.length > 0 ? (
          <div className="mt-4 space-y-2">
            {lookupMatches.map((match) => (
              <div
                key={match.familyId}
                className={`w-full rounded-xl border px-4 py-3 text-left transition-all ${selectedFamilyId === match.familyId ? "border-primary bg-white shadow-sm" : "border-gray-100 bg-white/70 hover:border-primary/40"}`}
              >
                <button
                  type="button"
                  onClick={() => setSelectedFamilyId(match.familyId)}
                  className="w-full text-left"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-bold text-irongrey">
                        {match.familyName}
                      </p>
                      <p className="text-xs text-irongrey/50">
                        {match.parentFirstName || match.parentLastName
                          ? `${match.parentFirstName} ${match.parentLastName}`.trim()
                          : "Matched family record"}
                        {match.studentCount > 0 ? ` - ${match.studentCount} student${match.studentCount === 1 ? "" : "s"}` : ""}
                      </p>
                      <p className="mt-1 text-[11px] font-semibold text-irongrey/45">
                        <span className="font-bold text-primary">{lookupMatchLabel(match)}</span>
                        {" - "}
                        Checking {match.existingApplication?.academicYear || academicYear}
                      </p>
                      <p className="text-[11px] font-semibold text-irongrey/45">
                        {lookupMatchDescription(match)}
                      </p>
                      {match.inviteApplicationId ? (
                        <p className="text-[11px] font-semibold text-irongrey/45">
                          Pre-submission invite record - no family record yet.
                        </p>
                      ) : null}
                      {match.existingApplication ? (
                        <div className="mt-2 space-y-1">
                          <p className="text-[11px] font-bold text-primary">
                            Existing {match.existingApplication.academicYear || academicYear} financial aid application: {INVITE_LIFECYCLE_LABELS[normalizeStatusToken(match.existingApplication.status)] || match.existingApplication.status}.
                          </p>
                          {isInviteLifecycleStatus(match.existingApplication.status) ? (
                            <p className="text-[11px] font-semibold text-irongrey/60">
                              This family already has an in-progress application for {match.existingApplication.academicYear || academicYear} (status: {INVITE_LIFECYCLE_LABELS[normalizeStatusToken(match.existingApplication.status)] || match.existingApplication.status}). Sending will resend the link to {match.existingApplication.invitedEmail || match.email || normalizedEmail || "the invited parent"}.
                            </p>
                          ) : (
                            <p className="text-[11px] font-semibold text-irongrey/60">
                              This family already has an application for this academic year. Open this application instead of sending another invite.
                            </p>
                          )}
                        </div>
                      ) : null}
                    </div>
                    <span className="rounded-full bg-primary/10 px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-primary">
                      {selectedFamilyId === match.familyId ? "Selected" : "Use"}
                    </span>
                  </div>
                </button>
                {match.existingApplication && selectedFamilyId === match.familyId ? (
                  <div
                    data-testid="financial-aid-existing-application-actions"
                    className="mt-3 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3"
                  >
                    <button
                      type="button"
                      onClick={() => {
                        const existingApplicationId = readString(match.existingApplication?.applicationId);
                        if (!existingApplicationId) return;
                        handleClose();
                        navigate(toStaffDetailRoute(existingApplicationId));
                      }}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-primary/20 bg-white px-3 py-1.5 text-[11px] font-bold text-primary transition-colors hover:border-primary/40 hover:bg-primary/5"
                    >
                      <ExternalLink size={13} />
                      View Application
                    </button>
                    {isInviteLifecycleStatus(match.existingApplication.status) ? (
                      <span className="inline-flex items-center gap-1.5 rounded-lg bg-primary/10 px-3 py-1.5 text-[11px] font-bold text-primary">
                        <Mail size={13} />
                        Resend invite below
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ))}
            {lookupTotalCount > lookupMatches.length ? (
              <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-xs font-semibold text-amber-800">
                Showing {lookupMatches.length} of {lookupTotalCount}. Refine by adding last name.
              </p>
            ) : null}
            <button
              type="button"
              onClick={() => setSelectedFamilyId(CREATE_NEW_FAMILY_SELECTION)}
              className={`w-full rounded-xl border px-4 py-3 text-left transition-all ${selectedFamilyId === CREATE_NEW_FAMILY_SELECTION ? "border-primary bg-white shadow-sm" : "border-gray-100 bg-white/70 hover:border-primary/40"}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-bold text-irongrey">
                    Send as a new financial aid family
                  </p>
                  <p className="text-xs text-irongrey/50">
                    Use this when none of the matches are the family you want.
                  </p>
                </div>
                <span className="rounded-full bg-primary/10 px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-primary">
                  {selectedFamilyId === CREATE_NEW_FAMILY_SELECTION ? "Selected" : "Use"}
                </span>
              </div>
            </button>
          </div>
        ) : showLookupWarning ? (
          <div
            role="status"
            data-testid="financial-aid-family-lookup-warning"
            className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs font-semibold leading-5 text-amber-800"
          >
            We couldn't check existing families just now. Try again before sending so this invite can be matched correctly.
            <button
              type="button"
              onClick={() => {
                void familyLookup.refetch();
              }}
              className="mt-3 block rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-[11px] font-bold text-amber-900 transition-colors hover:bg-amber-100"
            >
              Retry lookup
            </button>
            {familyLookup.error?.message ? (
              <details className="mt-2 font-medium text-amber-900">
                <summary className="cursor-pointer font-bold">Technical details</summary>
                <p className="mt-1 break-words">{familyLookup.error.message}</p>
              </details>
            ) : null}
          </div>
        ) : (
          <p className="mt-4 text-xs font-semibold text-irongrey/50">
            No existing family matched this search. The invite will create a new pre-submission record.
          </p>
        )}
      </div>

      {!inviteResult && copyMessage ? (
        <div className="rounded-2xl border border-primary/10 bg-primary/5 p-4">
          <p className="text-sm font-bold text-irongrey">Invitation link ready</p>
          <p className="mt-1 text-xs font-semibold text-irongrey/60">
            This link opens the parent financial aid entry flow for {formData.academicYear.trim() || "the selected cycle"}. Families will authenticate before starting.
          </p>
          <p className="mt-3 text-xs font-bold text-primary break-all">
            {copyMessage}
          </p>
          {copyStatusMessage ? (
            <p
              className="mt-3 text-xs font-bold text-primary"
              data-testid="financial-aid-invite-copy-confirmation"
            >
              {copyStatusMessage}
            </p>
          ) : null}
        </div>
      ) : null}

      {inviteResult ? (
        <div className="rounded-2xl border border-aquamarine/20 bg-aquamarine/10 p-4">
          <p className="text-sm font-bold text-irongrey">
            Invite is ready for {inviteResult.email}
          </p>
          <p className="mt-1 text-xs font-semibold text-irongrey/60">
            Status: {inviteResult.statusLabel}. {inviteResult.emailQueued === false
              ? "Email was not sent from the system."
              : "Email request queued. If the family does not see it, use Copy Link."} Sending again will reuse this invite for the same email and school year.
          </p>
          {copyMessage ? (
            <p className="mt-3 text-xs font-bold text-primary break-all">
              {copyMessage}
            </p>
          ) : null}
          {copyStatusMessage ? (
            <p
              className="mt-3 text-xs font-bold text-primary"
              data-testid="financial-aid-invite-copy-confirmation"
            >
              {copyStatusMessage}
            </p>
          ) : null}
        </div>
      ) : null}

      {inviteResult?.emailQueued === false ? (
        <SupportErrorMessage
          role="status"
          technicalDetails={inviteResult.emailQueueReason ?? undefined}
        >
          {inviteResult.emailQueueMessage || "The invite was created, but the email was not queued. Use Copy Link to share this invite another way."}
        </SupportErrorMessage>
      ) : null}

      {submitError ? (
        <SupportErrorMessage technicalDetails={submitError.technicalDetails}>
          {submitError.message}
        </SupportErrorMessage>
      ) : null}
    </AppDialogFrame>
  );
};

interface HeaderCellProps {
  id: string;
  width: number;
  label: string;
  onResize: (id: string, e: React.MouseEvent) => void;
  onClick?: () => void;
  center?: boolean;
  right?: boolean;
}

const HeaderCell: React.FC<HeaderCellProps> = ({
  id,
  width,
  label,
  onResize,
  onClick,
  center,
  right,
}) => (
  <th
    className={`px-6 py-4 text-[10px] font-bold text-irongrey/40 uppercase tracking-widest relative group select-none ${center ? "text-center" : right ? "text-right" : "text-left"}`}
    style={{ width }}
  >
    <button
      type="button"
      className={`w-full truncate bg-transparent p-0 text-left ${right ? "pl-4" : "pr-4"} ${onClick ? "hover:text-irongrey" : ""}`}
      disabled={!onClick}
      onClick={onClick}
    >
      {label}
    </button>
    <div
      className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-primary/50 opacity-0 group-hover:opacity-100 z-10"
      onMouseDown={(e) => onResize(id, e)}
    />
  </th>
);

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  let colorClass = "bg-gray-100 text-irongrey/60";

  switch (status) {
    case "Submitted":
      colorClass = "bg-aquamarine/10 text-aquamarine";
      break;
    case "Applied":
      colorClass = "bg-aquamarine/10 text-aquamarine";
      break;
    case "Invited":
      colorClass = "bg-gray-100 text-irongrey/70";
      break;
    case "Opened":
      colorClass = "bg-sky-100 text-sky-700";
      break;
    case "Started":
      colorClass = "bg-amber-100 text-amber-700";
      break;
    case "In-Progress":
    case "Under Review":
      colorClass = "bg-primary/10 text-primary";
      break;
    case "Scheduled":
    case "More Info Needed":
      colorClass = "bg-strawberry/10 text-strawberry";
      break;
    case "Decision":
      colorClass = "bg-jungle/10 text-jungle";
      break;
    case "Accepted":
    case "Approved":
      colorClass = "bg-jungle text-white";
      break;
    case "Declined":
    case "Denied":
      colorClass = "bg-strawberry text-white";
      break;
    case "Archived":
      colorClass = "bg-gray-500 text-white";
      break;
    case "Enrolled":
      colorClass = "bg-primary text-white";
      break;
    case "Waitlisted":
      colorClass = "bg-amber-100 text-amber-600";
      break;
  }

  return (
    <span
      className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wide ${colorClass}`}
    >
      {status}
    </span>
  );
};

const StaleInviteIndicator: React.FC<{ compact?: boolean }> = ({ compact = false }) => (
  <span
    className={`inline-flex items-center gap-1 font-bold uppercase tracking-wide text-amber-700 ${
      compact ? "text-[10px]" : "text-[11px]"
    }`}
    title="Sent more than 14 days ago and not opened yet."
    aria-label="Stale invite. Sent more than 14 days ago and not opened yet."
  >
    <CircleAlert size={compact ? 12 : 14} aria-hidden="true" />
    Stale invite
  </span>
);

const QuickViewPanel: React.FC<{
  applicant: Applicant | null;
  onClose: () => void;
  onViewFull: () => void;
}> = ({ applicant, onClose, onViewFull }) => {
  const applicantId = readString(applicant?.id);
  const detailQuery = useGetFinancialAidApplicationForReviewQuery(applicantId, {
    skip: !applicantId,
  });
  const historyQuery = useGetFinancialAidStatusHistoryQuery(applicantId, {
    skip: !applicantId,
  });

  const detailRecord = asRecord(detailQuery.data as unknown);
  const applicationRecord = asRecord(detailRecord.application);
  const sections = asRecord(applicationRecord.sections);
  const childrenBlock = asRecord(sections.children);
  const children = asArray<Record<string, unknown>>(
    Array.isArray(childrenBlock.children)
      ? childrenBlock.children
      : applicationRecord.children,
  );
  const firstChild = asRecord(children[0]);

  const familyName =
    readString(applicationRecord.familyName) ||
    readString(detailRecord.familyName) ||
    readString(applicant?.name) ||
    "Name required";
  const applicationCode = readString(applicationRecord.code) || applicantId;
  const dateOfBirth = formatDate(
    firstChild.dateOfBirth ?? firstChild.birthDate ?? firstChild.dob,
  );
  const gradeApplying = normalizeGrade(
    firstChild.gradeApplyingFor ??
      firstChild.grade ??
      applicationRecord.gradeApplyingFor ??
      applicant?.grade,
  );
  const currentSchool = readString(
    firstChild.currentSchool ??
      firstChild.previousSchool ??
      applicationRecord.previousSchool,
  );

  const documents = useMemo(() => {
    const source = asArray<Record<string, unknown>>(
      asRecord(detailQuery.data as unknown).documents,
    );
    return source
      .map((documentRecord, index) => {
        const id =
          readString(documentRecord.id) ||
          readString(documentRecord.documentType) ||
          `document-${index}`;
        const type = readString(documentRecord.documentType).replace(/_/g, " ");
        const name =
          readString(documentRecord.fileName) ||
          readString(documentRecord.documentName) ||
          type ||
          "Document";
        const status =
          readString(documentRecord.status) ||
          (documentRecord.isUploaded ? "Uploaded" : "Missing");
        const uploadedAt = formatDate(
          documentRecord.uploadedAt ??
            documentRecord.updatedAt ??
            documentRecord.createdAt,
        );
        const downloadUrl = readString(
          documentRecord.downloadUrl ??
            documentRecord.url ??
            documentRecord.fileUrl ??
            documentRecord.signedUrl,
        );
        return {
          id,
          name,
          status,
          uploadedAt,
          downloadUrl,
        };
      })
      .slice(0, 5);
  }, [detailQuery.data]);

  const timeline = useMemo(() => {
    const historyEntries = asArray<Record<string, unknown>>(
      asRecord(historyQuery.data as unknown).entries,
    );
    const decisionEntries = asArray<Record<string, unknown>>(
      asRecord(detailQuery.data as unknown).decisionHistory,
    );
    const events: Array<{
      id: string;
      title: string;
      note: string;
      at: unknown;
      kind: "status" | "decision";
    }> = [];

    historyEntries.forEach((entry, index) => {
      const status = readString(entry.status).replace(/_/g, " ");
      if (!status) return;
      events.push({
        id: readString(entry.id) || `status-${index}`,
        title: `Status: ${status}`,
        note: readString(entry.note),
        at: entry.changedAt,
        kind: "status",
      });
    });

    decisionEntries.forEach((entry, index) => {
      const decisionType = readString(entry.decisionType).replace(/_/g, " ");
      if (!decisionType) return;
      events.push({
        id: readString(entry.id) || `decision-${index}`,
        title: `Decision: ${decisionType}`,
        note:
          readString(entry.overrideJustification) ||
          readString(entry.denialReason),
        at: entry.decidedAt,
        kind: "decision",
      });
    });

    return events
      .map((event) => ({
        ...event,
        timestamp: toEpochMs(event.at) ?? 0,
      }))
      .sort((left, right) => right.timestamp - left.timestamp)
      .slice(0, 6);
  }, [detailQuery.data, historyQuery.data]);

  if (!applicant) return null;

  return createPortal(
    <>
      <div
        data-passive-click-surface="true"
        className="fixed inset-0 z-[100] bg-irongrey/20 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <div className="fixed top-0 right-0 bottom-0 z-[101] w-full max-w-md bg-white shadow-2xl animate-in slide-in-from-right duration-300 flex flex-col">
        <div className="px-6 py-6 border-b border-gray-100 flex items-start justify-between bg-white shrink-0">
          <div>
            <h2 className="text-xl font-bold text-irongrey leading-tight">
              {familyName}
            </h2>
            <p className="text-[11px] font-medium text-irongrey/40 mt-1 uppercase tracking-wide">
              {applicationCode
                ? `Ref: ${applicationCode}`
                : "Reference unavailable"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onViewFull}
              className="text-xs font-bold text-irongrey border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50 transition-colors flex items-center gap-1.5"
            >
              View Full Profile
              <ExternalLink size={12} />
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-irongrey/40 hover:bg-gray-100 transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-8">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <UserCircle size={18} className="text-irongrey/40" />
              <h3 className="text-xs font-bold text-irongrey/40 uppercase tracking-widest">
                Student Information
              </h3>
            </div>
            <div className="bg-background-light border border-gray-100 rounded-2xl p-5">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h4 className="text-lg font-bold text-irongrey">
                    {applicant.name}
                  </h4>
                </div>
                <StatusBadge status={applicant.statusBadgeLabel ?? applicant.status} />
              </div>

              <div className="grid grid-cols-2 gap-y-4">
                <div>
                  <p className="text-[10px] font-bold text-irongrey/40 uppercase mb-0.5">
                    DOB
                  </p>
                  <p className="text-sm font-semibold text-irongrey">
                    {dateOfBirth || "Not available"}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-irongrey/40 uppercase mb-0.5">
                    Grade Applying
                  </p>
                  <p className="text-sm font-semibold text-irongrey">
                    {gradeApplying || "Not available"}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-[10px] font-bold text-irongrey/40 uppercase mb-0.5">
                    Current School
                  </p>
                  <p className="text-sm font-semibold text-irongrey">
                    {currentSchool || "Not available"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-3">
              <FileText size={18} className="text-irongrey/40" />
              <h3 className="text-xs font-bold text-irongrey/40 uppercase tracking-widest">
                Documents
              </h3>
            </div>
            {detailQuery.isLoading || detailQuery.isFetching ? (
              <p className="text-xs text-irongrey/40 font-semibold">
                Loading documents...
              </p>
            ) : null}
            {detailQuery.error ? (
              <SupportErrorMessage className="text-xs">
                Unable to load documents right now.
              </SupportErrorMessage>
            ) : null}
            {!detailQuery.isLoading &&
            !detailQuery.isFetching &&
            !detailQuery.error &&
            documents.length === 0 ? (
              <p className="text-xs text-irongrey/40 font-semibold">
                Document list is syncing.
              </p>
            ) : null}
            <div className="space-y-3">
              {documents.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-3 border border-gray-100 rounded-xl hover:border-primary/20 transition-colors group bg-white"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-strawberry/10 flex items-center justify-center text-strawberry">
                      <FileText size={16} />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-irongrey">
                        {doc.name}
                      </p>
                      <p className="text-[10px] text-irongrey/40 uppercase font-bold">
                        {doc.status}
                        {doc.uploadedAt ? ` • ${doc.uploadedAt}` : ""}
                      </p>
                    </div>
                  </div>
                  {doc.downloadUrl ? (
                    <a
                      href={doc.downloadUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-irongrey/20 group-hover:text-primary transition-colors"
                      aria-label={`Download ${doc.name}`}
                    >
                      <Download size={18} />
                    </a>
                  ) : (
                    <span className="text-[10px] font-bold uppercase tracking-wide text-irongrey/35">
                      No file
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-3">
              <Clock size={18} className="text-irongrey/40" />
              <h3 className="text-xs font-bold text-irongrey/40 uppercase tracking-widest">
                Application Timeline
              </h3>
            </div>
            {historyQuery.isLoading || historyQuery.isFetching ? (
              <p className="text-xs text-irongrey/40 font-semibold">
                Loading timeline...
              </p>
            ) : null}
            {historyQuery.error ? (
              <SupportErrorMessage className="text-xs">
                Unable to load timeline right now.
              </SupportErrorMessage>
            ) : null}
            {!historyQuery.isLoading &&
            !historyQuery.isFetching &&
            !historyQuery.error &&
            timeline.length === 0 ? (
              <p className="text-xs text-irongrey/40 font-semibold">
                Timeline entries are syncing.
              </p>
            ) : null}
            <div className="ml-2 pl-4 border-l-2 border-gray-100 space-y-6 py-1">
              {timeline.map((event) => (
                <article key={event.id} className="relative">
                  <div
                    className={`absolute -left-[21px] top-0 w-3 h-3 rounded-full ring-4 ring-white shadow-sm ${
                      event.kind === "decision" ? "bg-jungle" : "bg-primary"
                    }`}
                  />
                  <h4 className="text-sm font-bold text-irongrey">
                    {event.title}
                  </h4>
                  {event.note ? (
                    <p className="text-xs text-irongrey/60 mt-1">
                      {event.note}
                    </p>
                  ) : null}
                  <p className="text-xs text-irongrey/40 mt-0.5">
                    {formatDateTime(event.timestamp) || "Timestamp unavailable"}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
};

export const Dashboard: React.FC = () => {
  const reviewQueue = useListFinancialAidApplicationsForReviewQuery({
    limit: 200,
  });
  const settingsConfig = useGetSettingsModuleConfigQuery({
    moduleKey: "staff_settings",
  });
  const copyInviteLinkMutation = useCopyFinancialAidInviteLink();
  const resendInviteMutation = useResendFinancialAidInvite();
  const assignMutation = useAssignFinancialAidApplication();
  const beginReviewMutation = useBeginFinancialAidReview();
  const requestMoreInfoMutation = useRequestFinancialAidMoreInfo();
  const [pendingMoreInfoRequest, setPendingMoreInfoRequest] = useState<PendingMoreInfoRequest | null>(null);
  const [pendingMoreInfoMessage, setPendingMoreInfoMessage] = useState("");
  const [moreInfoEmailConfirmed, setMoreInfoEmailConfirmed] = useState(false);
  const pendingMoreInfoDetail = useGetFinancialAidApplicationForReviewQuery(
    pendingMoreInfoRequest?.applicant.id ?? "",
    { skip: !pendingMoreInfoRequest },
  );
  const isInitialQueueLoading =
    (reviewQueue.isLoading || reviewQueue.isFetching) &&
    !reviewQueue.data &&
    !reviewQueue.error;
  const reviewQueueErrorPresentation = useMemo(
    () =>
      reviewQueue.error
        ? toFinancialAidStaffError(
            reviewQueue.error,
            "Unable to load review queue right now.",
          )
        : null,
    [reviewQueue.error],
  );

  // --- State ---
  const [viewMode, setViewMode] = useState<DashboardViewMode>(() =>
    readDashboardViewMode(),
  );
  const dashboardRootRef = useRef<HTMLDivElement | null>(null);
  const [dashboardSurfaceWidth, setDashboardSurfaceWidth] = useState<number>(0);
  const boardViewportRef = useRef<HTMLDivElement | null>(null);
  const [boardColumnCount, setBoardColumnCount] = useState<BoardColumnCount>(4);

  // Persist viewMode
  useEffect(() => {
    persistDashboardViewMode(viewMode);
  }, [viewMode]);

  const liveApplicants = useMemo(
    () =>
      (reviewQueue.data?.items ?? [])
        .map(projectFinancialAidReviewListItemForDashboard)
        .filter((entry) => readString(entry.id).length > 0),
    [reviewQueue.data?.items],
  );

  const [items, setItems] = useState<Applicant[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const dragOriginRef = useRef<{ active: Applicant; items: Applicant[] } | null>(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const isMobile =
    dashboardSurfaceWidth > 0 ? dashboardSurfaceWidth < 1024 : false;
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [actionFeedback, setActionFeedback] = useState<{
    tone: "success" | "error";
    message: string;
    technicalDetails?: string;
  } | null>(null);

  useEffect(() => {
    const root = dashboardRootRef.current;
    if (!root) {
      return () => {};
    }

    const syncWidth = () => {
      setDashboardSurfaceWidth(Math.round(root.getBoundingClientRect().width));
    };

    syncWidth();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(syncWidth);
      observer.observe(root);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", syncWidth);
    return () => window.removeEventListener("resize", syncWidth);
  }, []);

  useEffect(() => {
    const viewport = boardViewportRef.current;
    if (!viewport) return;

    const updateColumns = () => {
      const availableWidth = readBoardAvailableWidth(viewport);
      setBoardColumnCount((previous) => {
        const next = resolveBoardColumnCount(availableWidth);
        return previous === next ? previous : next;
      });
    };

    updateColumns();

    const assistant = document.querySelector(
      '[data-testid="app-shell-assistant"]',
    ) as HTMLElement | null;

    if (typeof ResizeObserver === "undefined") {
      const handleResize = () => updateColumns();
      window.addEventListener("resize", handleResize);
      const assistantAttributeObserver = assistant
        ? new MutationObserver(() => updateColumns())
        : null;
      assistantAttributeObserver?.observe(assistant!, {
        attributes: true,
        attributeFilter: ["class", "style", "data-expanded"],
      });
      assistant?.addEventListener("transitionend", handleResize);
      return () => {
        window.removeEventListener("resize", handleResize);
        assistantAttributeObserver?.disconnect();
        assistant?.removeEventListener("transitionend", handleResize);
      };
    }

    let frameId: number | null = null;
    const scheduleUpdate = () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
      frameId = window.requestAnimationFrame(() => updateColumns());
    };
    const observer = new ResizeObserver((entries) => {
      if (entries.length === 0) return;
      scheduleUpdate();
    });
    observer.observe(viewport);
    const assistantResizeObserver = assistant
      ? new ResizeObserver(() => scheduleUpdate())
      : null;
    assistantResizeObserver?.observe(assistant!);
    const assistantAttributeObserver = assistant
      ? new MutationObserver(() => scheduleUpdate())
      : null;
    assistantAttributeObserver?.observe(assistant!, {
      attributes: true,
      attributeFilter: ["class", "style", "data-expanded"],
    });
    assistant?.addEventListener("transitionend", scheduleUpdate);
    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
      observer.disconnect();
      assistantResizeObserver?.disconnect();
      assistantAttributeObserver?.disconnect();
      assistant?.removeEventListener("transitionend", scheduleUpdate);
    };
  }, []);

  useEffect(() => {
    setItems(liveApplicants);
  }, [liveApplicants]);

  useEffect(() => {
    if (!actionFeedback || actionFeedback.tone !== "success") return;
    const timeoutId = window.setTimeout(() => setActionFeedback(null), 4000);
    return () => window.clearTimeout(timeoutId);
  }, [actionFeedback]);

  const pendingMoreInfoRecipient = useMemo(
    () => readFinancialAidReviewParentEmail(pendingMoreInfoDetail.data),
    [pendingMoreInfoDetail.data],
  );
  const pendingMoreInfoPreview = useMemo(() => {
    if (!pendingMoreInfoRequest) return null;
    const requestedInfo = pendingMoreInfoMessage.trim();
    const applicationLabel = pendingMoreInfoRequest.applicant.id;
    return {
      to: pendingMoreInfoRecipient || "Parent email on file",
      subject: `Additional Information Required — ${pendingMoreInfoRequest.applicant.name} (${applicationLabel})`,
      message: buildFinancialAidMoreInfoParentMessage({
        applicantName: pendingMoreInfoRequest.applicant.name,
        requestedInfo,
      }),
    };
  }, [pendingMoreInfoMessage, pendingMoreInfoRecipient, pendingMoreInfoRequest]);

  // Quick View & Menu State
  const [quickViewApplicant, setQuickViewApplicant] =
    useState<Applicant | null>(null);
  const [actionMenuState, setActionMenuState] = useState<{
    id: string;
    step: "main" | "assign";
    top: number;
    left: number;
    openUpward: boolean;
  } | null>(null);
  const [pendingInviteResend, setPendingInviteResend] = useState<Applicant | null>(null);
  const actionMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!actionMenuState) return undefined;
    const frame = requestAnimationFrame(() => {
      const first = actionMenuRef.current?.querySelector<HTMLButtonElement>("button:not([disabled])");
      first?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [actionMenuState?.id, actionMenuState?.step]);

  // Filters
  const [studentsFilter, setStudentsFilter] = useState("Students");
  const [gradeFilter, setGradeFilter] = useState("All Grades");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [assigneeFilter, setAssigneeFilter] = useState("All Staff");

  const isFiltered = useMemo(() => {
    return isFinancialAidBoardFiltered({
      searchQuery,
      studentsFilter,
      gradeFilter,
      statusFilter,
      assigneeFilter,
    });
  }, [assigneeFilter, gradeFilter, searchQuery, statusFilter, studentsFilter]);

  const resetFilters = () => {
    setStudentsFilter("Students");
    setGradeFilter("All Grades");
    setStatusFilter("All Status");
    setAssigneeFilter("All Staff");
  };

  const [columnTitles, setColumnTitles] = useState<Record<string, string>>({
    Applied: "Applied",
    "In-Progress": "In-Progress",
    Scheduled: "Scheduled",
    Decision: "Decision",
  });

  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({
    name: 240,
    students: 100,
    grade: 120,
    submitted: 150,
    status: 150,
    assignee: 180,
    interview: 200,
    actions: 140,
  });
  const enforceDesktopTableWidth = dashboardSurfaceWidth >= 1024;
  const listTableMinWidth = useMemo(
    () =>
      Object.values(columnWidths).reduce((total, width) => total + width, 0),
    [columnWidths],
  );

  const navigate = useNavigate();

  const configuredStaffOptions = useMemo(() => {
    const configRecord = asRecord(settingsConfig.data);
    const payload = asRecord(configRecord.payload);
    const staffRows = Array.isArray(payload.staff) ? payload.staff : [];
    return staffRows
      .map(toFinancialAidAssignableStaffOption)
      .filter((option): option is FinancialAidAssignableStaffOption => Boolean(option));
  }, [settingsConfig.data]);

  const configuredStaffNames = useMemo(
    () => configuredStaffOptions.map((option) => option.name),
    [configuredStaffOptions],
  );

  const assignableStaffOptions = useMemo(() => {
    return mergeFinancialAidAssignableStaffOptions(configuredStaffOptions, items);
  }, [configuredStaffOptions, items]);

  const staffMembers = useMemo(() => {
    const names = new Set<string>();
    configuredStaffNames.forEach((name) => names.add(name));
    items
      .map((entry) => readString(entry.assigneeName))
      .filter((name) => name.length > 0)
      .forEach((name) => names.add(name));
    return Array.from(names).sort((left, right) => left.localeCompare(right));
  }, [configuredStaffNames, items]);

  const staffAvatarByName = useMemo(() => {
    const map = new Map<string, string>();
    items.forEach((entry) => {
      const staffName = readString(entry.assigneeName);
      const staffAvatar = readString(entry.assigneeAvatar);
      if (staffName && staffAvatar && !map.has(staffName)) {
        map.set(staffName, staffAvatar);
      }
    });
    return map;
  }, [items]);

  const resolveStaffAvatar = (staffName: string): string =>
    staffAvatarByName.get(staffName) ||
    createAvatarDataUri(`staff:${staffName}`, staffName);

  const dashboardFiltersContent = (
    <FilterBarSection mobile={isMobile}>
      <FilterDropdownField
        label="Students"
        value={studentsFilter}
        options={toSelectOptions(["Students", "1", "2", "3", "4", "5+"])}
        onChange={setStudentsFilter}
        mobile={isMobile}
        className={
          isMobile
            ? "w-full rounded-xl border border-gray-100 bg-white"
            : "border-r border-gray-100 pr-1"
        }
        placeholder="Students"
        testId="fa-dashboard-filter-students"
      />
      <FilterDropdownField
        label="Grade"
        value={gradeFilter}
        options={toSelectOptions([
          "All Grades",
          "K",
          "1",
          "2",
          "3",
          "4",
          "5",
          "6",
          "7",
          "8",
        ])}
        onChange={setGradeFilter}
        mobile={isMobile}
        className={
          isMobile
            ? "w-full rounded-xl border border-gray-100 bg-white"
            : "border-r border-gray-100 pr-1"
        }
        placeholder="All Grades"
        testId="fa-dashboard-filter-grade"
      />
      <FilterDropdownField
        label="Status"
        value={statusFilter}
        options={toSelectOptions([
          "All Status",
          "Invite Pipeline",
          "Invited",
          "Opened",
          "Started",
          "Submitted",
          "Under Review",
          "More Info Needed",
          "Approved",
          "Denied",
        ])}
        onChange={setStatusFilter}
        mobile={isMobile}
        className={
          isMobile
            ? "w-full rounded-xl border border-gray-100 bg-white"
            : "border-r border-gray-100 pr-1"
        }
        placeholder="Status"
        testId="fa-dashboard-filter-status"
      />
      <FilterDropdownField
        label="Assignee"
        value={assigneeFilter}
        options={toSelectOptions(["All Staff", ...staffMembers])}
        onChange={setAssigneeFilter}
        mobile={isMobile}
        className={
          isMobile
            ? "w-full rounded-xl border border-gray-100 bg-white"
            : "pr-1"
        }
        placeholder="Assignee"
        testId="fa-dashboard-filter-assignee"
      />
      {isFiltered ? (
        <button
          type="button"
          onClick={resetFilters}
          className={`${isMobile ? "col-span-2 w-full flex justify-center py-2 text-xs" : "ml-2 mr-3 p-1"} rounded-lg border border-gray-100 bg-gray-50 text-irongrey/40 transition-all cursor-pointer hover:text-strawberry`}
          title="Reset Filters"
        >
          {isMobile ? "Reset All Filters" : <X size={14} />}
        </button>
      ) : null}
    </FilterBarSection>
  );

  // Handle "Archived" logic
  useEffect(() => {
    if (statusFilter === "Archived") {
      setViewMode("list");
    }
  }, [statusFilter]);

  // --- DnD Sensors ---
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        actionMenuRef.current &&
        !actionMenuRef.current.contains(event.target as Node)
      ) {
        setActionMenuState(null);
      }
    };
    const closeActionMenu = () => setActionMenuState(null);
    // INT-819: the capture-phase scroll listener also fires for scrolls inside
    // the menu's own options list; closing there made long assignee lists
    // unscrollable (pointer and keyboard). Only close for outside scrolls.
    const handleScrollAway = (event: Event) => {
      const target = event.target;
      if (
        actionMenuRef.current &&
        target instanceof Node &&
        actionMenuRef.current.contains(target)
      ) {
        return;
      }
      closeActionMenu();
    };
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("resize", closeActionMenu);
    window.addEventListener("scroll", handleScrollAway, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("resize", closeActionMenu);
      window.removeEventListener("scroll", handleScrollAway, true);
    };
  }, []);

  // --- Handlers ---
  const handleApplicantClick = (applicationId?: string) => {
    const resolvedId =
      readString(applicationId) ||
      readString(activeId);
    if (!resolvedId) return;
    navigate(toStaffDetailRoute(resolvedId));
  };

  const handleQuickView = (applicant: Applicant) => {
    setQuickViewApplicant(applicant);
    setActionMenuState(null);
  };

  const handleAssign = async (
    applicantId: string,
    option: { id: string | null; name: string } | null,
  ) => {
    setActionMenuState(null);
    if (option && !option.id) {
      setActionFeedback({
        tone: "error",
        message: "This staff record does not include a valid user ID yet.",
      });
      return;
    }
    const previousItems = items;
    setItems((prev) =>
      prev.map((a) =>
        a.id === applicantId ? { ...a, assigneeName: option?.name ?? "" } : a,
      ),
    );
    try {
      await assignMutation.mutateAsync({
        applicationId: applicantId,
        assignedTo: option ? option.id : null,
        assigneeName: option?.name ?? null,
      });
      setActionFeedback({
        tone: "success",
        message: option ? `Assigned to ${option.name}.` : "Assignment cleared.",
      });
      void reviewQueue.refetch();
    } catch (error) {
      setItems(previousItems);
      setActionFeedback({
        tone: "error",
        ...toFinancialAidStaffError(
          error,
          "We couldn't update the assignment right now. Please try again.",
        ),
      });
    }
  };

  const handleCopyInviteLink = async (applicant: Applicant) => {
    setActionMenuState(null);
    try {
      const result = await copyInviteLinkMutation.mutateAsync({
        applicationId: applicant.id,
      });
      const inviteUrl = readString(asRecord(result).inviteUrl);
      if (inviteUrl && typeof navigator !== "undefined" && navigator.clipboard) {
        await navigator.clipboard.writeText(inviteUrl);
      }
      setActionFeedback({
        tone: "success",
        message: inviteUrl
          ? `Invite link copied for ${applicant.name}.`
          : `Invite link logged for ${applicant.name}.`,
      });
      void reviewQueue.refetch();
    } catch (error) {
      setActionFeedback({
        tone: "error",
        ...toFinancialAidStaffError(
          error,
          "We couldn't copy the invite link right now. Please try again.",
        ),
      });
    }
  };

  const handleConfirmInviteResend = async () => {
    const applicant = pendingInviteResend;
    if (!applicant) return;
    setPendingInviteResend(null);
    try {
      const result = await resendInviteMutation.mutateAsync({ applicationId: applicant.id });
      const resendRecord = asRecord(result);
      if (resendRecord.emailQueued === false) {
        setActionFeedback({
          tone: "error",
          message: readString(resendRecord.emailQueueMessage) || "The invite was updated, but the email was not queued. Use Copy Link to share the invite another way.",
          technicalDetails: readString(resendRecord.emailQueueReason) || undefined,
        });
        void reviewQueue.refetch();
        return;
      }
      setActionFeedback({
        tone: "success",
        message: `Email request queued for ${applicant.invitedEmail || applicant.name}. If the family does not see it, use Copy Link.`,
      });
      void reviewQueue.refetch();
    } catch (error) {
      setActionFeedback({
        tone: "error",
        ...toFinancialAidStaffError(
          error,
          "We couldn't resend the invite right now. Please try again.",
        ),
      });
    }
  };

  const handleResendInvite = async (applicant: Applicant) => {
    setActionMenuState(null);
    setPendingInviteResend(applicant);
  };

  const closeMoreInfoApproval = () => {
    if (pendingMoreInfoRequest?.revertItems) {
      setItems(pendingMoreInfoRequest.revertItems);
    }
    setPendingMoreInfoRequest(null);
    setPendingMoreInfoMessage("");
    setMoreInfoEmailConfirmed(false);
  };

  const handleConfirmMoreInfoRequest = async (sendExternalParentEmail: boolean) => {
    if (!pendingMoreInfoRequest) return;
    const requestedInfo = pendingMoreInfoMessage.trim();
    if (!requestedInfo) {
      setActionFeedback({
        tone: "error",
        message: "Enter the details the family should provide before continuing.",
      });
      return;
    }
    if (sendExternalParentEmail && !moreInfoEmailConfirmed) {
      setActionFeedback({
        tone: "error",
        message: "Please confirm the email preview before queueing a parent email.",
      });
      return;
    }

    const { applicant, transition } = pendingMoreInfoRequest;
    try {
      await requestMoreInfoMutation.mutateAsync({
        applicationId: applicant.id,
        message: requestedInfo,
        deadlineDays: transition.deadlineDays,
        sendExternalParentEmail,
        externalParentEmailApproved: sendExternalParentEmail,
      });
      const now = Date.now();
      setItems((current) =>
        current.map((item) =>
          item.id === applicant.id
            ? {
                ...item,
                status: "Scheduled",
                rawStatus: "more_info_needed",
                statusLabel: "More Info Needed",
                statusBadgeLabel: "More Info Needed",
                lastActivityAt: now,
                lastActivityLabel: "Just now",
              }
            : item,
        ),
      );
      setPendingMoreInfoRequest(null);
      setPendingMoreInfoMessage("");
      setMoreInfoEmailConfirmed(false);
      setActionFeedback({
        tone: "success",
        message: sendExternalParentEmail
          ? `${applicant.name} moved to More Info Needed and parent email queued.`
          : `${applicant.name} moved to More Info Needed. Parent email was not sent.`,
      });
      void reviewQueue.refetch();
    } catch (error) {
      if (pendingMoreInfoRequest.revertItems) {
        setItems(pendingMoreInfoRequest.revertItems);
      }
      setPendingMoreInfoRequest(null);
      setPendingMoreInfoMessage("");
      setMoreInfoEmailConfirmed(false);
      setActionFeedback({
        tone: "error",
        ...toFinancialAidStaffError(
          error,
          "We couldn't save this financial aid status change. Please try again.",
        ),
      });
    }
  };

  const persistBoardStatusTransition = async (
    applicant: Applicant,
    targetStatus: Applicant["status"] | ColumnStatus,
    revertItems: Applicant[] | null = null,
  ): Promise<boolean> => {
    const transition = resolveFinancialAidBoardStatusTransition(applicant, targetStatus);

    if (transition.kind === "noop") {
      return true;
    }

    if (transition.kind === "unsupported") {
      setActionFeedback({ tone: "error", message: transition.reason });
      return false;
    }

    if (transition.kind === "requestMoreInfo") {
      setPendingMoreInfoRequest({
        applicant,
        targetStatus,
        transition,
        revertItems,
      });
      setPendingMoreInfoMessage(transition.requestedInfo);
      setMoreInfoEmailConfirmed(false);
      setActionFeedback(null);
      return true;
    }

    try {
      if (transition.kind === "beginReview") {
        await beginReviewMutation.mutateAsync({ applicationId: applicant.id });
      }
      setActionFeedback({ tone: "success", message: transition.successMessage });
      void reviewQueue.refetch();
      return true;
    } catch (error) {
      setActionFeedback({
        tone: "error",
        ...toFinancialAidStaffError(
          error,
          "We couldn't save this financial aid status change. Please try again.",
        ),
      });
      return false;
    }
  };

  const resolveDragTargetStatus = (
    activeApplicant: Applicant | undefined,
    overId: string,
    isOverApplicant: boolean,
  ): Applicant["status"] | ColumnStatus | undefined => {
    const statusMap: Record<string, ColumnStatus> = {
      Applied: "Applied",
      "In-Progress": "In-Progress",
      Scheduled: "Scheduled",
      Decision: "Decision",
    };

    if (overId in statusMap) {
      return statusMap[overId];
    }

    if (!isOverApplicant) {
      return undefined;
    }

    const overApplicant = items.find((a) => a.id === overId);
    if (!overApplicant) {
      return undefined;
    }

    const overStatusGroup = getFinancialAidBoardStatusGroup(overApplicant.status);
    if (overStatusGroup !== "Decision") {
      return overStatusGroup as Applicant["status"] | ColumnStatus;
    }

    if (activeApplicant && getFinancialAidBoardStatusGroup(activeApplicant.status) === "Decision") {
      return activeApplicant.status;
    }

    return "Decision";
  };

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };

  const parseGrade = (gradeStr: string): number => {
    const val = gradeStr.replace("Grade ", "").trim();
    if (val === "K") return 0;
    const num = parseInt(val);
    return isNaN(num) ? 99 : num;
  };

  const matchesStatusFilter = (applicant: Applicant): boolean => {
    if (statusFilter === "All Status") return true;
    if (statusFilter === "Invite Pipeline") return isInviteApplicant(applicant);
    const statusFilterToken = normalizeStatusToken(statusFilter);
    if (isInviteLifecycleStatus(statusFilterToken)) {
      return normalizeStatusToken(applicant.rawStatus) === statusFilterToken;
    }
    if (statusFilter === "Submitted") {
      return isFinancialAidSubmittedStatus(applicant);
    }
    if (statusFilter === "Under Review") {
      if (isInviteApplicant(applicant)) return false;
      return (
        applicant.status === "In-Progress" ||
        applicant.statusLabel === "Under Review" ||
        applicant.statusLabel === "Docs Pending" ||
        applicant.statusLabel === "App Review"
      );
    }
    if (statusFilter === "More Info Needed") {
      return (
        applicant.status === "Scheduled" ||
        applicant.statusLabel === "More Info Needed" ||
        applicant.statusLabel === "Urgent Priority" ||
        applicant.statusLabel === "Family Interview"
      );
    }
    if (statusFilter === "Approved")
      return (
        applicant.status === "Accepted" || applicant.statusLabel === "Approved"
      );
    if (statusFilter === "Denied")
      return (
        applicant.status === "Declined" ||
        applicant.status === "Denied" ||
        applicant.statusLabel === "Denied"
      );
    if (statusFilter === "Archived") return applicant.status === "Archived";
    return (
      applicant.status === statusFilter ||
      applicant.statusLabel === statusFilter
    );
  };

  const handleArchiveAll = (_status: string) => {
    setActionFeedback({
      tone: "error",
      message: "Bulk archive is disabled until it can run through the audited financial-aid workflow.",
    });
  };

  const handleSortByDate = (status: string) => {
    setItems((prev) => {
      const matches = prev.filter((a) =>
        status === "Decision"
          ? getFinancialAidBoardStatusGroup(a.status) === "Decision"
          : a.status === status,
      );
      const nonMatches = prev.filter(
        (a) =>
          !(status === "Decision"
            ? getFinancialAidBoardStatusGroup(a.status) === "Decision"
            : a.status === status),
      );

      matches.sort(
        (a, b) => getApplicantActivityMs(b) - getApplicantActivityMs(a),
      );

      return [...nonMatches, ...matches];
    });
  };

  const handleMoveAll = (
    _currentStatus: string,
    _newStatus: Applicant["status"],
  ) => {
    setActionFeedback({
      tone: "error",
      message: "Bulk status moves are disabled until they can run through the audited financial-aid workflow.",
    });
  };

  const handleRenameColumn = (columnId: string, newTitle: string) => {
    setColumnTitles((prev) => ({ ...prev, [columnId]: newTitle }));
  };

  // --- Derived State ---
  const filteredAndSortedApplicants = useMemo(() => {
    let result = [...items];

    if (statusFilter === "Archived") {
      result = result.filter((a) => a.status === "Archived");
    } else {
      result = result.filter((a) => a.status !== "Archived");
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (query === "family") {
        // Canonical QA scenarios use "Family" as a broad search probe.
        // Treat it as a wildcard against family-scoped records.
        return result;
      }
      result = result.filter(
        (a) =>
          a.name.toLowerCase().includes(query) ||
          a.id.toLowerCase().includes(query) ||
          a.status.toLowerCase().includes(query) ||
          readString(a.statusLabel).toLowerCase().includes(query) ||
          readString(a.statusBadgeLabel).toLowerCase().includes(query) ||
          readString(a.invitedEmail).toLowerCase().includes(query),
      );
    }

    if (gradeFilter !== "All Grades") {
      result = result.filter((a) => a.grade.includes(gradeFilter));
    }

    if (statusFilter !== "All Status") {
      result = result.filter(matchesStatusFilter);
    }

    if (assigneeFilter !== "All Staff") {
      result = result.filter((a) => a.assigneeName === assigneeFilter);
    }

    if (studentsFilter !== "Students") {
      if (studentsFilter === "5+") {
        result = result.filter((a) => (a.studentsCount || 0) >= 5);
      } else {
        result = result.filter(
          (a) => (a.studentsCount || 0) === parseInt(studentsFilter),
        );
      }
    }

    if (sortKey && (viewMode === "list" || statusFilter === "Archived")) {
      result.sort((a, b) => {
        let valA: any, valB: any;

        switch (sortKey) {
          case "name":
            valA = a.name.toLowerCase();
            valB = b.name.toLowerCase();
            break;
          case "studentsCount":
            valA = a.studentsCount || 1;
            valB = b.studentsCount || 1;
            break;
          case "grade":
            valA = parseGrade(a.grade);
            valB = parseGrade(b.grade);
            break;
          case "date":
            valA = getApplicantActivityMs(a);
            valB = getApplicantActivityMs(b);
            break;
          case "status":
            valA = getApplicantStatusLabel(a).toLowerCase();
            valB = getApplicantStatusLabel(b).toLowerCase();
            break;
          case "assigneeName":
            valA = (a.assigneeName || "").toLowerCase();
            valB = (b.assigneeName || "").toLowerCase();
            break;
          case "interviewDate":
            const statA = a.interviewDate ? 0 : 1;
            const statB = b.interviewDate ? 0 : 1;
            if (statA !== statB) {
              valA = statA;
              valB = statB;
            } else {
              valA = a.interviewDate ? new Date(a.interviewDate).getTime() : 0;
              valB = b.interviewDate ? new Date(b.interviewDate).getTime() : 0;
            }
            break;
          default:
            valA = "";
            valB = "";
        }

        if (valA < valB) return sortDirection === "asc" ? -1 : 1;
        if (valA > valB) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [
    items,
    searchQuery,
    studentsFilter,
    gradeFilter,
    statusFilter,
    assigneeFilter,
    sortKey,
    sortDirection,
    viewMode,
  ]);

  const dashboardMetrics = useMemo(() => {
    const activeItems = items.filter((applicant) => applicant.status !== "Archived");
    return {
      total: activeItems.length,
      invited: activeItems.filter(
        (applicant) => normalizeStatusToken(applicant.rawStatus) === "invited",
      ).length,
      submitted: activeItems.filter(isFinancialAidSubmittedStatus).length,
    };
  }, [items]);

  const startResizing = (colId: string, e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = columnWidths[colId];

    const onMouseMove = (moveEvent: MouseEvent) => {
      const currentWidth = startWidth + (moveEvent.clientX - startX);
      setColumnWidths((prev) => ({
        ...prev,
        [colId]: Math.max(50, currentWidth),
      }));
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  // --- DnD Logic ---
  const shouldRenderGrid = viewMode === "grid" && statusFilter !== "Archived";
  const boardDragDisabled = isFinancialAidFilteredBoardDragReadOnly(shouldRenderGrid, isFiltered);

  const handleDragStart = (event: DragStartEvent) => {
    if (boardDragDisabled) {
      setActionFeedback({
        tone: "error",
        message: "Filtered board view is read-only. Reset filters to move applications.",
      });
      return;
    }
    const nextActiveId = event.active.id as string;
    setActiveId(nextActiveId);
    const activeApplicant = items.find((item) => item.id === nextActiveId);
    dragOriginRef.current = activeApplicant ? { active: activeApplicant, items } : null;
    if (viewMode === "grid") {
      setSortKey(null);
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    if (boardDragDisabled) return;
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    if (activeId === overId) return;

    const isActiveApplicant = active.data.current?.type === "Applicant";
    const isOverApplicant = over.data.current?.type === "Applicant";

    if (!isActiveApplicant) return;

    const isOverColumn = ["Applied", "In-Progress", "Scheduled", "Decision"].includes(overId);
    const activeOriginal = dragOriginRef.current?.active ?? items.find((a) => a.id === activeId);
    const targetStatus = resolveDragTargetStatus(activeOriginal, overId, Boolean(isOverApplicant));

    if (activeOriginal && targetStatus) {
      const transition = resolveFinancialAidBoardStatusTransition(activeOriginal, targetStatus);
      if (transition.kind === "unsupported") {
        return;
      }
    }

    const activeCurrentIndex = items.findIndex((a) => a.id === activeId);
    const overItemIndex = items.findIndex((a) => a.id === overId);
    if (activeCurrentIndex === -1) return;

    if (isOverColumn && targetStatus) {
      setItems((prev) => {
        const currentIndex = prev.findIndex((a) => a.id === activeId);
        if (currentIndex === -1) return prev;
        const activeItem = prev[currentIndex];
        if (getFinancialAidBoardStatusGroup(activeItem.status) !== getFinancialAidBoardStatusGroup(targetStatus)) {
          const newItems = [...prev];
          newItems[currentIndex] = {
            ...activeItem,
            status: targetStatus as Applicant["status"],
          };
          return newItems;
        }
        return prev;
      });
      return;
    }

    if (isOverApplicant && targetStatus && overItemIndex !== -1) {
      setItems((prev) => {
        const currentIndex = prev.findIndex((a) => a.id === activeId);
        const targetIndex = prev.findIndex((a) => a.id === overId);
        if (currentIndex === -1 || targetIndex === -1) return prev;
        const activeItem = prev[currentIndex];
        const newItems = [...prev];
        const isStatusChange =
          getFinancialAidBoardStatusGroup(activeItem.status) !== getFinancialAidBoardStatusGroup(targetStatus);

        if (isStatusChange) {
          newItems[currentIndex] = {
            ...activeItem,
            status: targetStatus as Applicant["status"],
          };
        }
        return arrayMove(newItems, currentIndex, targetIndex);
      });
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    if (boardDragDisabled) return;

    const origin = dragOriginRef.current;
    dragOriginRef.current = null;

    if (!over) {
      if (origin) setItems(origin.items);
      return;
    }

    const activeId = active.id as string;
    const overId = over.id as string;

    if (activeId === overId) return;

    const activeApplicant = origin?.active ?? items.find((a) => a.id === activeId);
    const isOverApplicant = over.data.current?.type === "Applicant";
    const targetStatus = resolveDragTargetStatus(activeApplicant, overId, Boolean(isOverApplicant));

    if (activeApplicant && targetStatus) {
      const transition = resolveFinancialAidBoardStatusTransition(activeApplicant, targetStatus);
      if (transition.kind === "unsupported") {
        if (origin) setItems(origin.items);
        setActionFeedback({ tone: "error", message: transition.reason });
        return;
      }
      if (transition.kind !== "noop") {
        void persistBoardStatusTransition(activeApplicant, targetStatus, origin?.items ?? null).then((persisted) => {
          if (!persisted && origin) {
            setItems(origin.items);
          }
        });
        return;
      }
    }

    const activeIndex = items.findIndex((a) => a.id === activeId);
    const overIndex = items.findIndex((a) => a.id === overId);

    if (activeIndex !== -1 && overIndex !== -1) {
      setItems((prev) => arrayMove(prev, activeIndex, overIndex));
    }
  };

  const dropAnimation: DropAnimation = {
    sideEffects: defaultDropAnimationSideEffects({
      styles: {
        active: {
          opacity: "0.5",
        },
      },
    }),
  };

  const getColumnApplicants = (statusGroup: string) => {
    if (statusGroup === "Decision") {
      return filteredAndSortedApplicants.filter((a) =>
        getFinancialAidBoardStatusGroup(a.status) === "Decision",
      );
    }
    return filteredAndSortedApplicants.filter((a) => a.status === statusGroup);
  };

  const activeApplicant = useMemo(
    () => items.find((a) => a.id === activeId),
    [activeId, items],
  );

  return (
    <div
      id="main"
      ref={dashboardRootRef}
      data-testid="financial-aid-staff-dashboard-root"
      data-extracted-from="owned:ystnj/financial-aid/v2-1/staff-dashboard"
      aria-busy={isInitialQueueLoading}
      className="font-sans flex flex-col overflow-hidden h-full min-h-0"
    >
      <DndContext
        sensors={sensors}
        collisionDetection={financialAidBoardCollisionDetection}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <AddApplicantModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onFeedback={setActionFeedback}
          onCreated={() => {
            void reviewQueue.refetch();
          }}
        />
        <QuickViewPanel
          applicant={quickViewApplicant}
          onClose={() => setQuickViewApplicant(null)}
          onViewFull={() => {
            const targetId = readString(quickViewApplicant?.id);
            setQuickViewApplicant(null);
            if (!targetId) return;
            navigate(toStaffDetailRoute(targetId));
          }}
        />
        <AppDialogFrame
          open={Boolean(pendingInviteResend)}
          onClose={() => setPendingInviteResend(null)}
          title="Resend Financial Aid Invite"
          description="Confirm before queueing another parent email."
          testId="financial-aid-resend-invite-confirmation"
          maxWidthClassName="max-w-lg"
          titleClassName="text-xl"
          descriptionClassName="text-xs font-medium text-irongrey/40"
          bodyPaddingClassName="p-6"
          footerPaddingClassName="px-6 py-4"
          footer={
            <>
              <button
                type="button"
                onClick={() => setPendingInviteResend(null)}
                disabled={resendInviteMutation.isPending}
                className="px-4 py-2 text-sm font-bold text-irongrey/60 transition-colors hover:text-irongrey disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleConfirmInviteResend()}
                disabled={resendInviteMutation.isPending}
                className="flex items-center gap-2 rounded-xl bg-primary px-5 py-2 text-sm font-bold text-white shadow-lg shadow-primary/20 transition-all hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60"
              >
                <RefreshCw size={16} />
                {resendInviteMutation.isPending ? "Queueing..." : "Confirm resend"}
              </button>
            </>
          }
        >
          {pendingInviteResend ? (
            <div className="space-y-3">
              <p className="text-sm font-semibold leading-6 text-irongrey/70">
                This will send another financial aid invite email for <span className="font-bold text-irongrey">{pendingInviteResend.name}</span>.
              </p>
              <div className="rounded-xl border border-primary/10 bg-primary/5 px-4 py-3 text-xs font-semibold text-primary">
                Recipient: {pendingInviteResend.invitedEmail || "parent email on the invite"}
              </div>
              <p className="text-xs font-medium leading-5 text-irongrey/50">
                The original invite link will be reused, so this will not create a duplicate application.
              </p>
            </div>
          ) : null}
        </AppDialogFrame>
        <AppDialogFrame
          open={Boolean(pendingMoreInfoRequest)}
          onClose={closeMoreInfoApproval}
          title="Request More Information"
          description="Review the parent-facing message before queueing an external email."
          testId="financial-aid-more-info-email-approval-modal"
          maxWidthClassName="max-w-xl"
          titleClassName="text-xl"
          descriptionClassName="text-xs font-medium text-irongrey/40"
          bodyPaddingClassName="p-6"
          footerPaddingClassName="px-6 py-4"
          footer={
            <>
              <button
                type="button"
                onClick={closeMoreInfoApproval}
                disabled={requestMoreInfoMutation.isPending}
                className="px-4 py-2 text-sm font-bold text-irongrey/60 transition-colors hover:text-irongrey disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleConfirmMoreInfoRequest(false)}
                disabled={requestMoreInfoMutation.isPending}
                className="rounded-xl border border-primary/20 bg-white px-4 py-2 text-sm font-bold text-primary transition-all hover:border-primary/40 hover:bg-primary/5 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Move without email
              </button>
              <button
                type="button"
                onClick={() => void handleConfirmMoreInfoRequest(true)}
                disabled={requestMoreInfoMutation.isPending || !moreInfoEmailConfirmed}
                className="flex items-center gap-2 rounded-xl bg-primary px-5 py-2 text-sm font-bold text-white shadow-lg shadow-primary/20 transition-all hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Mail size={16} />
                {requestMoreInfoMutation.isPending ? "Queueing..." : "Queue Email"}
              </button>
            </>
          }
        >
          {pendingMoreInfoRequest && pendingMoreInfoPreview ? (
            <div className="space-y-4">
              <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-4">
                <p className="text-[10px] font-bold uppercase tracking-widest text-irongrey/40">
                  To
                </p>
                <p className="mt-1 text-sm font-bold text-irongrey">
                  {pendingMoreInfoPreview.to}
                </p>
                {pendingMoreInfoDetail.isLoading || pendingMoreInfoDetail.isFetching ? (
                  <p className="mt-1 text-xs font-semibold text-irongrey/40">
                    Checking parent contact...
                  </p>
                ) : null}
              </div>
              <label className="block">
                <span className="text-[10px] font-bold uppercase tracking-widest text-irongrey/40">
                  Requested information
                  <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[9px] text-amber-800">
                    Required
                  </span>
                </span>
                <textarea
                  data-testid="financial-aid-more-info-drag-message"
                  required
                  aria-required="true"
                  value={pendingMoreInfoMessage}
                  onChange={(event) => setPendingMoreInfoMessage(event.target.value)}
                  rows={4}
                  className="mt-2 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-semibold leading-6 text-irongrey outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/10"
                  placeholder="Describe the documents or details the family should provide."
                />
              </label>
              <div className="rounded-xl border border-gray-100 bg-white p-4">
                <p className="text-[10px] font-bold uppercase tracking-widest text-irongrey/40">
                  Subject
                </p>
                <p className="mt-1 text-sm font-bold text-irongrey">
                  {pendingMoreInfoPreview.subject}
                </p>
                <p className="mt-4 whitespace-pre-line text-sm font-semibold leading-6 text-irongrey/70">
                  {pendingMoreInfoPreview.message}
                </p>
              </div>
              <label className="flex items-start gap-3 rounded-xl border border-primary/10 bg-primary/5 p-4">
                <input
                  type="checkbox"
                  checked={moreInfoEmailConfirmed}
                  onChange={(event) => setMoreInfoEmailConfirmed(event.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="text-xs font-semibold leading-5 text-irongrey/70">
                  I reviewed this parent-facing message and approve queueing an external email for this more-info request.
                </span>
              </label>
            </div>
          ) : null}
        </AppDialogFrame>

        <header className="bg-background-light border-b border-gray-100 pb-4 flex-shrink-0 flex flex-col gap-4">
          <div className="flex items-center justify-between gap-3 w-full">
            {/* Search & Filters Container */}
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <SearchFilterControl
                inputTestId="fa-dashboard-search-input"
                toggleTestId="fa-dashboard-filter-toggle-button"
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search by code, family, or status"
                toggleActive={!isMobile && showFilters}
                onToggleFilters={() => setShowFilters((current) => !current)}
                className={
                  isMobile
                    ? "w-[170px] max-w-[170px] min-w-0"
                    : "w-full max-w-[320px] min-w-0"
                }
              />

              {/* Desktop Filter Drawer */}
              {!isMobile ? (
                <div
                  aria-hidden={!showFilters}
                  className={`flex items-center rounded-2xl border border-gray-100 bg-white p-1 shadow-sm transition-all ease-in-out duration-300 overflow-visible ${showFilters ? "max-w-4xl opacity-100 translate-x-0 ml-3" : "max-w-0 opacity-0 -translate-x-4 overflow-hidden pointer-events-none"}`}
                >
                  {dashboardFiltersContent}
                </div>
              ) : null}
            </div>

            {/* Right side buttons */}
            <div className="flex items-center gap-3 shrink-0">
              <div className="bg-white p-1 rounded-lg flex shadow-sm border border-gray-100">
                <button
                  type="button"
                  aria-label="Show financial aid board view"
                  aria-pressed={viewMode === "grid"}
                  title="Board view"
                  onClick={() => {
                    if (statusFilter !== "Archived") {
                      setViewMode("grid");
                      setSortKey(null);
                    }
                  }}
                  disabled={statusFilter === "Archived"}
                  className={`p-2 px-3 rounded-md transition-all ${viewMode === "grid" ? "bg-primary/10 text-primary shadow-sm" : "text-irongrey/40 hover:bg-gray-50"} ${statusFilter === "Archived" ? "opacity-30 cursor-not-allowed" : ""}`}
                >
                  <LayoutGrid
                    size={20}
                    className={viewMode === "grid" ? "fill-current" : ""}
                  />
                </button>
                <button
                  type="button"
                  aria-label="Show financial aid list view"
                  aria-pressed={viewMode === "list"}
                  title="List view"
                  onClick={() => {
                    setViewMode("list");
                    setSortKey("name");
                  }}
                  className={`p-2 px-3 rounded-md transition-all ${viewMode === "list" ? "bg-primary/10 text-primary shadow-sm" : "text-irongrey/40 hover:bg-gray-50"}`}
                >
                  <ListIcon
                    size={20}
                    className={viewMode === "list" ? "fill-current" : ""}
                  />
                </button>
              </div>
              <button
                type="button"
                aria-label="Invite financial aid family"
                title="Invite financial aid family"
                onClick={() => setIsAddModalOpen(true)}
                className="bg-primary hover:bg-primary-hover text-white p-2 rounded-lg shadow-lg shadow-primary/20 transition-all"
              >
                <Plus size={24} />
              </button>
            </div>
          </div>

          {isInitialQueueLoading && (
            <p className="text-xs font-semibold text-irongrey/60">
              Loading review queue...
            </p>
          )}
          {actionFeedback?.tone === "error" ? (
            <SupportErrorMessage
              className="mt-2 max-w-xl text-xs"
              data-testid="financial-aid-action-toast"
              technicalDetails={actionFeedback.technicalDetails}
            >
              {actionFeedback.message}
            </SupportErrorMessage>
          ) : actionFeedback ? (
            <div
              data-testid="financial-aid-action-toast"
              className="mt-2 max-w-xl rounded-xl border border-aquamarine/20 bg-aquamarine/10 px-4 py-3 text-xs font-bold text-irongrey"
            >
              {actionFeedback.message}
            </div>
          ) : null}

          {reviewQueueErrorPresentation ? null : (
            <div
              data-testid="financial-aid-dashboard-metrics"
              className="flex flex-wrap items-center gap-2"
              aria-label="Financial aid dashboard summary"
            >
              <button
                type="button"
                onClick={() => setStatusFilter("All Status")}
                aria-pressed={statusFilter === "All Status"}
                data-testid="financial-aid-dashboard-metric-total"
                className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-bold transition-colors ${
                  statusFilter === "All Status"
                    ? "border-primary/30 bg-primary/10 text-primary"
                    : "border-gray-100 bg-white text-irongrey/60 hover:border-primary/20 hover:text-primary"
                }`}
              >
                <ListIcon size={14} aria-hidden="true" />
                Total
                <span className="rounded-md bg-white/80 px-1.5 py-0.5 text-[11px] text-irongrey">
                  {dashboardMetrics.total}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setStatusFilter("Invited")}
                aria-pressed={statusFilter === "Invited"}
                data-testid="financial-aid-dashboard-metric-invited"
                className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-bold transition-colors ${
                  statusFilter === "Invited"
                    ? "border-primary/30 bg-primary/10 text-primary"
                    : "border-gray-100 bg-white text-irongrey/60 hover:border-primary/20 hover:text-primary"
                }`}
              >
                <Mail size={14} aria-hidden="true" />
                Invited
                <span className="rounded-md bg-white/80 px-1.5 py-0.5 text-[11px] text-irongrey">
                  {dashboardMetrics.invited}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setStatusFilter("Submitted")}
                aria-pressed={statusFilter === "Submitted"}
                data-testid="financial-aid-dashboard-metric-new"
                className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-bold transition-colors ${
                  statusFilter === "Submitted"
                    ? "border-primary/30 bg-primary/10 text-primary"
                    : "border-gray-100 bg-white text-irongrey/60 hover:border-primary/20 hover:text-primary"
                }`}
              >
                <FileText size={14} aria-hidden="true" />
                Submitted
                <span className="rounded-md bg-white/80 px-1.5 py-0.5 text-[11px] text-irongrey">
                  {dashboardMetrics.submitted}
                </span>
              </button>
            </div>
          )}

          {isMobile && showFilters ? (
            <div className="animate-in slide-in-from-top-2 duration-200">
              {dashboardFiltersContent}
            </div>
          ) : null}
        </header>

        <main
          ref={boardViewportRef}
          data-testid="financial-aid-dashboard-board-scroll"
          className="flex-1 overflow-x-auto overflow-y-auto px-0 pb-0 pt-0 mb-6 bg-background-light"
        >
          {reviewQueueErrorPresentation ? (
            <div
              data-testid="financial-aid-dashboard-error-state"
              className="flex min-h-[260px] items-start justify-center px-4 py-8"
            >
              <div className="w-full max-w-2xl">
                <SupportErrorMessage
                  technicalDetails={reviewQueueErrorPresentation.technicalDetails}
                >
                  {reviewQueueErrorPresentation.message}
                </SupportErrorMessage>
                <button
                  type="button"
                  data-testid="financial-aid-dashboard-retry-button"
                  onClick={() => void reviewQueue.refetch()}
                  className="mt-4 inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-bold text-white shadow-lg shadow-primary/20 transition-all hover:bg-primary-hover"
                >
                  <RefreshCw size={16} aria-hidden="true" />
                  Retry
                </button>
              </div>
            </div>
          ) : isInitialQueueLoading ? (
            shouldRenderGrid ? (
              <FinancialAidDashboardSkeleton
                columnCount={boardColumnCount}
              />
            ) : (
              <FinancialAidTableSkeleton />
            )
          ) : shouldRenderGrid ? (
            <div className="flex h-full flex-col gap-2">
              {boardDragDisabled ? (
                <div
                  data-testid="financial-aid-filtered-board-drag-disabled"
                  className="rounded-xl border border-primary/10 bg-primary/5 px-4 py-3 text-xs font-bold text-irongrey"
                >
                  Filtered board view is read-only. Reset filters to move applications.
                </div>
              ) : null}
              <div
                data-testid="financial-aid-dashboard-grid"
                data-column-count={boardColumnCount}
                className="grid w-full gap-2 items-start pb-10 min-h-0 flex-1 overflow-y-visible"
                style={boardGridStyle(boardColumnCount)}
              >
                {(
                  ["Applied", "In-Progress", "Scheduled", "Decision"] as const
                ).map((status) => (
                  <KanbanColumn
                    key={status}
                    id={status}
                    title={columnTitles[status]}
                    color={
                      status === "Applied"
                        ? "aquamarine"
                        : status === "In-Progress"
                          ? "primary"
                          : status === "Scheduled"
                            ? "strawberry"
                            : "jungle"
                    }
                    count={getColumnApplicants(status).length}
                    applicants={getColumnApplicants(status)}
                    onCardClick={handleApplicantClick}
                    listTestId={
                      status === "Applied"
                        ? "financial-aid-dashboard-list"
                        : undefined
                    }
                    onAddApplicant={() => setIsAddModalOpen(true)}
                    onArchiveAll={() => handleArchiveAll(status)}
                    onSortByDate={() => handleSortByDate(status)}
                    onMoveAll={(newStat) => handleMoveAll(status, newStat)}
                    onRenameTitle={(newTitle) =>
                      handleRenameColumn(status, newTitle)
                    }
                    cardDragDisabled={boardDragDisabled}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden h-full flex flex-col">
              <div className="overflow-x-auto overflow-y-auto flex-1 pb-24">
                <table
                  className="table-sticky-last-action-col w-full text-left border-collapse table-auto"
                  style={
                    enforceDesktopTableWidth
                      ? { minWidth: `${Math.floor(listTableMinWidth)}px` }
                      : undefined
                  }
                >
                  <thead className="sticky top-0">
                    <tr className="border-b border-gray-200 bg-gray-50/50">
                      <HeaderCell
                        id="name"
                        width={columnWidths.name}
                        onResize={startResizing}
                        onClick={() => handleSort("name")}
                        label="Family Name"
                      />
                      <HeaderCell
                        id="students"
                        width={columnWidths.students}
                        onResize={startResizing}
                        onClick={() => handleSort("studentsCount")}
                        label="Students"
                        center
                      />
                      <HeaderCell
                        id="grade"
                        width={columnWidths.grade}
                        onResize={startResizing}
                        onClick={() => handleSort("grade")}
                        label="Grades"
                      />
                      <HeaderCell
                        id="submitted"
                        width={columnWidths.submitted}
                        onResize={startResizing}
                        onClick={() => handleSort("date")}
                        label="Last Activity"
                      />
                      <HeaderCell
                        id="status"
                        width={columnWidths.status}
                        onResize={startResizing}
                        onClick={() => handleSort("status")}
                        label="Status"
                      />
                      <HeaderCell
                        id="assignee"
                        width={columnWidths.assignee}
                        onResize={startResizing}
                        onClick={() => handleSort("assigneeName")}
                        label="Assignee"
                      />
                      <HeaderCell
                        id="interview"
                        width={columnWidths.interview}
                        onResize={startResizing}
                        onClick={() => handleSort("interviewDate")}
                        label="Interview"
                      />
                      <HeaderCell
                        id="actions"
                        width={columnWidths.actions}
                        onResize={startResizing}
                        label="Actions"
                        right
                      />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredAndSortedApplicants.map((applicant) => (
                      <tr
                        key={applicant.id}
                        onClick={() => handleApplicantClick(applicant.id)}
                        className="hover:bg-gray-50 transition-colors group cursor-pointer h-16 border-b border-gray-200"
                      >
                        <td
                          className="px-6 py-4 overflow-hidden"
                          style={{ width: columnWidths.name }}
                        >
                          <div className="flex items-center gap-3 overflow-hidden whitespace-nowrap text-ellipsis">
                            <img
                              alt={applicant.name}
                              className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                              src={applicant.avatar}
                            />
                            <div className="overflow-hidden">
                              <p className="font-bold text-sm text-irongrey truncate">
                                {applicant.name}
                              </p>
                              <p className="text-xs text-irongrey/40 truncate">
                                ID: #{applicant.id}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td
                          className="px-6 py-4 text-center overflow-hidden whitespace-nowrap text-ellipsis"
                          style={{ width: columnWidths.students }}
                        >
                          <span className="bg-gray-100 text-irongrey px-2 py-0.5 rounded text-xs font-bold">
                            {applicant.studentsCount ?? 0}
                          </span>
                        </td>
                        <td
                          className="px-6 py-4 overflow-hidden whitespace-nowrap text-ellipsis"
                          style={{ width: columnWidths.grade }}
                        >
                          <span className="text-xs font-medium text-irongrey truncate">
                            {applicant.grade}
                          </span>
                        </td>
                        <td
                          className="px-6 py-4 text-xs text-irongrey/60 overflow-hidden whitespace-nowrap text-ellipsis"
                          style={{ width: columnWidths.submitted }}
                        >
                          <div>
                            <p>{applicant.lastActivityLabel || applicant.date}</p>
                            {applicant.staleInvite ? (
                              <p className="mt-1">
                                <StaleInviteIndicator compact />
                              </p>
                            ) : null}
                          </div>
                        </td>
                        <td
                          className="px-6 py-4 overflow-hidden whitespace-nowrap text-ellipsis"
                          style={{ width: columnWidths.status }}
                        >
                          <StatusBadge status={applicant.statusBadgeLabel ?? applicant.status} />
                        </td>
                        <td
                          className="px-6 py-4 overflow-hidden whitespace-nowrap text-ellipsis"
                          style={{ width: columnWidths.assignee }}
                        >
                          <div className="flex items-center gap-2 overflow-hidden">
                            <img
                              alt={applicant.assigneeName}
                              className="w-5 h-5 rounded-full flex-shrink-0"
                              src={applicant.assigneeAvatar}
                            />
                            <span className="text-xs font-medium truncate">
                              {applicant.assigneeName}
                            </span>
                          </div>
                        </td>
                        <td
                          className="px-6 py-4 overflow-hidden whitespace-nowrap text-ellipsis"
                          style={{ width: columnWidths.interview }}
                        >
                          {applicant.interviewDate ? (
                            <span className="text-xs font-bold text-strawberry truncate">
                              {applicant.interviewDate}, 10:00 AM
                            </span>
                          ) : getFinancialAidBoardStatusGroup(applicant.status) === "Decision" ? (
                            <span className="text-xs font-medium text-irongrey/40">
                              Completed
                            </span>
                          ) : applicant.status === "Archived" ? (
                            <span className="text-xs font-medium text-irongrey/40">
                              -
                            </span>
                          ) : (
                            <button className="text-xs font-bold text-primary border border-primary/30 px-3 py-1 rounded-lg hover:bg-primary hover:text-white transition-all whitespace-nowrap">
                              Schedule
                            </button>
                          )}
                        </td>
                        <td
                          className="px-6 py-4 text-right overflow-visible"
                          style={{ width: columnWidths.actions }}
                        >
                          <div>
                            <button
                              type="button"
                              aria-label={`Open actions for ${applicant.name}`}
                              aria-expanded={actionMenuState?.id === applicant.id}
                              title={`Actions for ${applicant.name}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (actionMenuState?.id === applicant.id) {
                                  setActionMenuState(null);
                                  return;
                                }
                                const rect =
                                  e.currentTarget.getBoundingClientRect();
                                const openUpward =
                                  rect.bottom + 320 > window.innerHeight;
                                setActionMenuState({
                                  id: applicant.id,
                                  step: "main",
                                  top: openUpward
                                    ? rect.top + window.scrollY - 4
                                    : rect.bottom + window.scrollY + 4,
                                  left: rect.right + window.scrollX,
                                  openUpward,
                                });
                              }}
                              className="text-irongrey/40 hover:text-primary p-2 -mr-2 rounded-full hover:bg-gray-100 transition-colors"
                            >
                              <MoreHorizontal size={20} />
                            </button>

                            {actionMenuState?.id === applicant.id &&
                            typeof document !== "undefined"
                              ? createPortal(
                                  <div
                                    ref={actionMenuRef}
                                    role="menu"
                                    aria-label={`Actions for ${applicant.name}`}
                                    className="absolute w-48 rounded-xl border border-gray-100 bg-white py-1.5 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.15)] z-[9999] animate-in fade-in zoom-in-95 duration-100 origin-top-right"
                                    style={{
                                      top: actionMenuState.top,
                                      left: actionMenuState.left,
                                      transform: actionMenuState.openUpward
                                        ? "translateX(-100%) translateY(-100%)"
                                        : "translateX(-100%)",
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    onKeyDown={(event) => {
                                      if (event.key === "Escape") {
                                        event.preventDefault();
                                        setActionMenuState(null);
                                        return;
                                      }
                                      if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
                                        return;
                                      }
                                      const container = actionMenuRef.current;
                                      if (!container) return;
                                      const menuItems = Array.from(
                                        container.querySelectorAll<HTMLButtonElement>("button:not([disabled])"),
                                      );
                                      if (menuItems.length === 0) return;
                                      event.preventDefault();
                                      const activeIndex = menuItems.findIndex(
                                        (item) => item === document.activeElement,
                                      );
                                      const nextIndex =
                                        event.key === "Home"
                                          ? 0
                                          : event.key === "End"
                                            ? menuItems.length - 1
                                            : event.key === "ArrowDown"
                                              ? (activeIndex + 1) % menuItems.length
                                              : activeIndex <= 0
                                                ? menuItems.length - 1
                                                : activeIndex - 1;
                                      const nextItem = menuItems[nextIndex];
                                      if (!nextItem) return;
                                      // INT-819: keep keyboard-focused options
                                      // visible inside the scrollable list.
                                      nextItem.focus({ preventScroll: true });
                                      nextItem.scrollIntoView?.({ block: "nearest" });
                                    }}
                                  >
                                    {actionMenuState.step === "main" ? (
                                      <>
                                        <button
                                          onClick={() =>
                                            setActionMenuState({
                                              ...actionMenuState,
                                              step: "assign",
                                            })
                                          }
                                          className="w-full text-left px-4 py-2.5 text-xs font-bold text-irongrey hover:bg-gray-50 flex items-center gap-3 transition-colors"
                                        >
                                          <UserPlus
                                            size={14}
                                            className="text-irongrey/60"
                                          />
                                          Assign to...
                                        </button>
                                        {isInviteApplicant(applicant) ? (
                                          <>
                                            <button
                                              onClick={() =>
                                                void handleCopyInviteLink(applicant)
                                              }
                                              disabled={copyInviteLinkMutation.isPending}
                                              className="w-full text-left px-4 py-2.5 text-xs font-bold text-irongrey hover:bg-gray-50 flex items-center gap-3 transition-colors disabled:opacity-50"
                                            >
                                              <Link2
                                                size={14}
                                                className="text-irongrey/60"
                                              />
                                              Copy invite link
                                            </button>
                                            <button
                                              onClick={() =>
                                                void handleResendInvite(applicant)
                                              }
                                              disabled={resendInviteMutation.isPending}
                                              className="w-full text-left px-4 py-2.5 text-xs font-bold text-irongrey hover:bg-gray-50 flex items-center gap-3 transition-colors disabled:opacity-50"
                                            >
                                              <RefreshCw
                                                size={14}
                                                className="text-irongrey/60"
                                              />
                                              Resend invite
                                            </button>
                                          </>
                                        ) : null}
                                        <button
                                          onClick={() =>
                                            handleQuickView(applicant)
                                          }
                                          className="w-full text-left px-4 py-2.5 text-xs font-bold text-irongrey hover:bg-gray-50 flex items-center gap-3 transition-colors"
                                        >
                                          <Eye
                                            size={14}
                                            className="text-irongrey/60"
                                          />
                                          Quick view
                                        </button>
                                      </>
                                    ) : (
                                      <>
                                        <div className="mb-1 flex items-center gap-2 border-b border-gray-50 px-4 py-2">
                                          <button
                                            type="button"
                                            aria-label="Back to financial aid applicant actions"
                                            onClick={() =>
                                              setActionMenuState({
                                                ...actionMenuState,
                                                step: "main",
                                              })
                                            }
                                            className="text-irongrey/40 hover:text-primary"
                                          >
                                            <ChevronDown
                                              size={14}
                                              className="rotate-90"
                                            />
                                          </button>
                                          <span className="text-[10px] font-bold uppercase tracking-widest text-irongrey/40">
                                            Select Staff
                                          </span>
                                        </div>
                                        <div className="max-h-[280px] overflow-y-auto" role="menu" aria-label="Assign financial aid application">
                                          {applicant.assigneeName ? (
                                            <button
                                              role="menuitem"
                                              onClick={() =>
                                                void handleAssign(applicant.id, null)
                                              }
                                              className="w-full text-left px-4 py-2 text-xs font-medium hover:bg-gray-50 flex items-center gap-2 transition-colors text-irongrey/70"
                                            >
                                              Unassign
                                            </button>
                                          ) : null}
                                          {assignableStaffOptions.length > 0 ? (
                                            assignableStaffOptions.map((option) => (
                                              <button
                                                key={option.name}
                                                role="menuitem"
                                                disabled={!option.id}
                                                title={!option.id ? "Staff record does not include a valid user ID" : undefined}
                                                onClick={() =>
                                                  void handleAssign(
                                                    applicant.id,
                                                    option,
                                                  )
                                                }
                                                className={`w-full text-left px-4 py-2 text-xs font-medium hover:bg-gray-50 flex items-center gap-2 transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${applicant.assigneeName === option.name ? "text-primary bg-primary/5" : "text-irongrey"}`}
                                              >
                                                <div className="w-5 h-5 rounded-full bg-gray-200 shrink-0">
                                                  <img
                                                    src={resolveStaffAvatar(
                                                      option.name,
                                                    )}
                                                    className="w-full h-full rounded-full object-cover"
                                                    alt=""
                                                  />
                                                </div>
                                                {option.name}
                                              </button>
                                            ))
                                          ) : (
                                            <div className="px-4 py-2 text-xs text-irongrey/60">
                                              Staff assignments are syncing.
                                            </div>
                                          )}
                                        </div>
                                      </>
                                    )}
                                  </div>,
                                  document.body,
                                )
                              : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </main>

        <DragOverlay dropAnimation={dropAnimation}>
          {activeApplicant ? (
            <div className="transform scale-105 opacity-90 cursor-grabbing">
              <SortableApplicantCard
                applicant={activeApplicant}
                onClick={() => {}}
              />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
};
